import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { 
    Check, ArrowLeft, Phone, Smartphone, 
    ShieldCheck, Globe, User, Building2, Zap, Loader2, Copy,
    Users, AlertTriangle, Info, X, Link2Off, HelpCircle, Settings, Save, MoreVertical, Pencil
} from 'lucide-react';
import { Tooltip, TooltipContent as BillingTooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import ConfirmDialog from "@/components/ConfirmDialog";
import mpesaLogo from '../../assets/mpesa.png';
import { auth, db, functions } from '@/firebase/config';
import { doc, getDoc, setDoc, updateDoc, arrayUnion, arrayRemove, onSnapshot, serverTimestamp, Timestamp, collection, query, where, getDocs, runTransaction, limit as firestoreLimit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import WorkspaceLoader from './WorkspaceLoader';
import { cn } from "@/lib/utils";
import { SUBSCRIPTION_TIERS } from '@/config/subscriptionPlans';
import { isFacultyPlanScope, isIndividualPlanScope, getPlanDisplayName, isTrialPlan } from '@/utils/planHelpers';

const tooltipClasses = "bg-slate-900 dark:bg-[#0a1024] text-zinc-100 border border-slate-700/50 dark:border-white/10 rounded-none px-3 py-2 shadow-2xl text-[10px] font-sans";
const BillingBillingTooltipContent = ({ className, children, ...props }: React.ComponentProps<typeof BillingTooltipContent>) => (
  <BillingTooltipContent className={cn(tooltipClasses, className)} {...props}>
    {children}
  </BillingTooltipContent>
);

const IndependentBillingView = () => {
    const { toast } = useToast();
    const [view, setView] = useState<'pricing' | 'payment'>('pricing');
    const [selectedPlan, setSelectedPlan] = useState<'free' | 'individual' | 'school'>('free');
    const [billingCycle, setBillingCycle] = useState<'monthly' | 'termly' | 'yearly'>('monthly');
    const [method, setMethod] = useState<'card' | 'mpesa'>('card');
    const [activeTier, setActiveTier] = useState<'tier1' | 'tier2'>('tier1');
    const [loadingPricing, setLoadingPricing] = useState(true);
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    
    // Faculty Join Flow States
    const [facultyCode, setFacultyCode] = useState('');
    const [isJoiningFaculty, setIsJoiningFaculty] = useState(false);
    const [copiedFacultyCode, setCopiedFacultyCode] = useState(false);

    // Linked teacher tracking (for principals)
    type LinkedTeacher = { uid: string; email: string; displayName?: string; joinedAt: any };
    const [linkedTeachers, setLinkedTeachers] = useState<LinkedTeacher[]>([]);
    const [isRevokingAccess, setIsRevokingAccess] = useState<string | null>(null); // uid being revoked

    // Faculty link status (for teachers who joined via code)
    const [linkedTo, setLinkedTo] = useState<{ principalUid: string; principalEmail: string; institutionName?: string; organizationId?: string } | null>(null);
    const [linkRevoked, setLinkRevoked] = useState(false);
    
    // Principal / Faculty Management States
    const [userPlan, setUserPlan] = useState('');
    const [userStatus, setUserStatus] = useState('');
    const [userExpiry, setUserExpiry] = useState<Date | null>(null);
    const [trialClaimedAt, setTrialClaimedAt] = useState<Date | null>(null);
    const [allowedEmails, setAllowedEmails] = useState<string[]>([]);
    const [adminEmails, setAdminEmails] = useState<string[]>([]);
    const [newEmail, setNewEmail] = useState('');
    const [maxSeats, setMaxSeats] = useState<number>(10);
    const [institutionName, setInstitutionName] = useState('');
    const [organizationId, setOrganizationId] = useState('');
    const [isSavingOrgDetails, setIsSavingOrgDetails] = useState(false);
    const [isEditingOrgName, setIsEditingOrgName] = useState(false);
    const [editOrgNameValue, setEditOrgNameValue] = useState('');
    const [orgMenuOpen, setOrgMenuOpen] = useState(false);
    const [copiedOrgId, setCopiedOrgId] = useState(false);
    const [isActivatingTrial, setIsActivatingTrial] = useState(false);
    const [trialDays, setTrialDays] = useState<number>(14);
    const [debugLogs, setDebugLogs] = useState<string[]>([]);
    const [showDebugPanel, setShowDebugPanel] = useState(false);
    const [showFacultyPanel, setShowFacultyPanel] = useState(() => {
        return new URLSearchParams(window.location.search).get('panel') === 'faculty';
    });

    // Clean URL query param after reading it
    useEffect(() => {
        if (window.location.search.includes('panel=faculty')) {
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, []);

    // Reactive auth state tracking
    const [currentUid, setCurrentUid] = useState<string | null>(null);


    const [state, setState] = useState({
        focus: '',
        phone: ''
    });

    // PesaPal card billing state
    const [cardFirstName, setCardFirstName] = useState('');
    const [cardLastName, setCardLastName] = useState('');
    const [cardEmail, setCardEmail] = useState('');
    const [cardPhone, setCardPhone] = useState('');
    const [pesapalIframeUrl, setPesapalIframeUrl] = useState<string | null>(null);
    const [pesapalOrderId, setPesapalOrderId] = useState<string | null>(null);
    const cardBillingPrefilled = useRef(false);

    // M-Pesa payment lifecycle tracking
    const [pendingTransactionId, setPendingTransactionId] = useState<string | null>(null);
    const [paymentStatus, setPaymentStatus] = useState<'idle' | 'processing' | 'completed' | 'failed'>('idle');
    const [stkPushSent, setStkPushSent] = useState(false);
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [successBannerDismissed, setSuccessBannerDismissed] = useState(false);
    const [transactions, setTransactions] = useState<any[]>([]);
    const [loadingTransactions, setLoadingTransactions] = useState(false);
    const [showPaymentHistory, setShowPaymentHistory] = useState(false);

    // Payment confirmation dialog — fires when Firestore confirms activation
    const [paymentConfirmDialog, setPaymentConfirmDialog] = useState<{
        open: boolean;
        plan: string;
        expiry: Date | null;
        method: string;
    }>({ open: false, plan: '', expiry: null, method: '' });

    const [adminConfirmDialog, setAdminConfirmDialog] = useState<{
        open: boolean;
        email: string;
        isCurrentlyAdmin: boolean;
    }>({ open: false, email: '', isCurrentlyAdmin: false });

    const [pricingState, setPricingState] = useState({
        monthly: { individual: "1,500", school: "20,000", label: "KSH/MO", cycleLabel: "Monthly Cycle", durationDays: "30" },
        termly: { individual: "4,000", school: "55,000", label: "KSH/TERM", cycleLabel: "Termly Cycle", durationDays: "90" },
        yearly: { individual: "12,000", school: "180,000", label: "KSH/YR", cycleLabel: "Yearly Cycle", durationDays: "365" }
    });

    const addLog = (label: string, data?: any) => {
        const ts = new Date().toISOString().replace('T', ' ').slice(0, 23);
        const entry = data !== undefined
            ? `[${ts}] ${label}\n${JSON.stringify(data, null, 2)}`
            : `[${ts}] ${label}`;
        setDebugLogs(prev => [entry, ...prev]);
        console.log(`[IndBilling] ${label}`, data ?? '');
    };

    const formatSubscriptionDate = (date: Date | null) => {
        if (!date) return 'pending confirmation';
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
    };

    const toDateSafe = (value: any): Date | null => {
        if (!value) return null;
        if (typeof value.toDate === 'function') return value.toDate();
        if (value instanceof Date) return value;
        if (typeof value === 'number') return new Date(value);
        return null;
    };

    const generateOrgId = () => {
        // 6-char base-36 timestamp suffix + 4-char random = 10-char total
        // Probability of collision across 1M orgs is < 0.0001%
        const tsBase = Date.now().toString(36).toUpperCase().slice(-4);
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let rand = '';
        for (let i = 0; i < 4; i++) {
            rand += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return `FAC-${tsBase}${rand}`;
    };

    const handleSaveOrgDetails = async () => {
        const uid = currentUid || auth.currentUser?.uid;
        if (!uid) {
            toast({ title: 'Not signed in', description: 'Please wait, signing in...', variant: 'destructive' });
            return;
        }

        if (!institutionName.trim()) {
            toast({ title: 'Name Required', description: 'Please enter your Institution Name. It is mandatory to set up your faculty.', variant: 'destructive' });
            return;
        }

        setIsSavingOrgDetails(true);
        try {
            let finalOrgId = organizationId;
            if (!finalOrgId) {
                finalOrgId = generateOrgId();
                setOrganizationId(finalOrgId);
            }
            const workspaceRef = doc(db, 'teaching_workspaces', uid);
            await updateDoc(workspaceRef, {
                institutionName: institutionName.trim(),
                organizationId: finalOrgId
            });
            toast({ title: 'Saved', description: 'Organization details updated.', duration: 3000 });
        } catch (err: any) {
            console.error('Failed to save org details', err);
            toast({ title: 'Error', description: err.message || 'Failed to update organization details.', variant: 'destructive' });
        } finally {
            setIsSavingOrgDetails(false);
        }
    };

    const handleUpdateOrgName = async () => {
        const uid = currentUid || auth.currentUser?.uid;
        if (!uid || !editOrgNameValue.trim()) return;
        setIsSavingOrgDetails(true);
        try {
            await updateDoc(doc(db, 'teaching_workspaces', uid), {
                institutionName: editOrgNameValue.trim()
            });
            setInstitutionName(editOrgNameValue.trim());
            setIsEditingOrgName(false);
            toast({ title: 'Updated', description: 'Institution name has been updated.', duration: 3000 });
        } catch (err: any) {
            toast({ title: 'Error', description: err.message || 'Failed to update name.', variant: 'destructive' });
        } finally {
            setIsSavingOrgDetails(false);
        }
    };

    const prefillCardBillingDetails = (details: { displayName?: string | null; email?: string | null; phoneNumber?: string | null }) => {
        if (cardBillingPrefilled.current) return;

        const email = details.email || '';
        const fallbackName = email ? email.split('@')[0].replace(/[._-]+/g, ' ') : '';
        const nameParts = (details.displayName || fallbackName).trim().split(/\s+/).filter(Boolean);
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ');
        const phoneNumber = details.phoneNumber || '';

        if (!firstName && !lastName && !email && !phoneNumber) return;

        setCardFirstName(prev => prev || firstName);
        setCardLastName(prev => prev || lastName);
        setCardEmail(prev => prev || email);
        setCardPhone(prev => prev || phoneNumber);
        if (phoneNumber) {
            setState(prev => ({ ...prev, phone: prev.phone || phoneNumber }));
        }
        cardBillingPrefilled.current = true;
    };

    useEffect(() => {
        const fetchPricing = async () => {
            addLog('🔍 Fetching pricing from systemConfig/independentPricing...');
            try {
                const docRef = doc(db, 'systemConfig', 'independentPricing');
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    addLog('✅ Pricing loaded', data);
                    setPricingState(prev => ({
                        monthly: { ...prev.monthly, individual: data.monthly?.individual ?? prev.monthly.individual, school: data.monthly?.school ?? prev.monthly.school, durationDays: data.monthly?.durationDays ?? prev.monthly.durationDays },
                        termly: { ...prev.termly, individual: data.termly?.individual ?? prev.termly.individual, school: data.termly?.school ?? prev.termly.school, durationDays: data.termly?.durationDays ?? prev.termly.durationDays },
                        yearly: { ...prev.yearly, individual: data.yearly?.individual ?? prev.yearly.individual, school: data.yearly?.school ?? prev.yearly.school, durationDays: data.yearly?.durationDays ?? prev.yearly.durationDays },
                    }));
                } else {
                    addLog('⚠️ WARN: systemConfig/independentPricing doc does not exist — using hardcoded defaults');
                }
            } catch (error: any) {
                addLog('❌ ERROR fetching pricing', { message: error.message, code: error.code });
                console.error("Error fetching independent pricing:", error);
            } finally {
                setLoadingPricing(false);
            }
        };
        fetchPricing();

        let unsubscribe: any;
        let unsubscribePrincipal: any;
        const unsubscribeAuth = auth.onAuthStateChanged((user) => {
            if (user) {
                addLog('🔐 Auth state: signed in', { uid: user.uid, email: user.email });
                setCurrentUid(user.uid);
                prefillCardBillingDetails(user);
                const userDocRef = doc(db, 'teaching_workspaces', user.uid);
                addLog(`📡 Attaching onSnapshot to teaching_workspaces/${user.uid}`);
                unsubscribe = onSnapshot(userDocRef, (snap) => {
                    if (snap.exists()) {
                        const data = snap.data();
                        addLog('📥 Workspace snapshot received', {
                            plan: data.subscription?.plan,
                            status: data.subscription?.status,
                            expiry: data.subscription?.expiry?.toDate?.()?.toISOString() ?? data.subscription?.expiry ?? null,
                            linkedTo: data.linkedTo?.principalUid ?? null,
                            linkedRevoked: data.linkedTo?.revoked ?? false,
                        });
                        setUserPlan(data.subscription?.plan || '');
                        setUserStatus(data.subscription?.status || '');
                        setAllowedEmails(data.subscription?.allowedEmails || []);
                        setAdminEmails(data.subscription?.adminEmails || []);
                        setMaxSeats(data.subscription?.maxSeats);
                        setLinkedTeachers(data.subscription?.linkedTeachers || []);
                        setInstitutionName(data.institutionName || '');
                        setOrganizationId(data.organizationId || '');
                        prefillCardBillingDetails({
                            displayName: data.displayName || data.name || data.ownerName,
                            email: data.email,
                            phoneNumber: data.phoneNumber || data.phone || data.billingPhone,
                        });

                        if (data.linkedTo?.principalUid) {
                            setLinkedTo({
                                principalUid: data.linkedTo.principalUid,
                                principalEmail: data.linkedTo.principalEmail || ''
                            });
                            if (data.linkedTo.revoked) {
                                setLinkRevoked(true);
                            } else {
                                setLinkRevoked(false);
                            }
                            
                            // Try to subscribe to principal's workspace (only works if they are an admin or owner)
                            if (!unsubscribePrincipal) {
                                const principalDocRef = doc(db, 'teaching_workspaces', data.linkedTo.principalUid);
                                unsubscribePrincipal = onSnapshot(principalDocRef, (pSnap) => {
                                    if (pSnap.exists()) {
                                        const pData = pSnap.data();
                                        setAdminEmails(pData.subscription?.adminEmails || []);
                                        // Hydrate institution name + org ID from principal for linked teachers
                                        setLinkedTo(prev => prev ? {
                                            ...prev,
                                            institutionName: pData.institutionName || '',
                                            organizationId: pData.organizationId || ''
                                        } : prev);
                                        // If this user is an admin, show the principal's lists for editing
                                        const userEmail = user.email || '';
                                        const isAdmin = userEmail && (pData.subscription?.adminEmails || []).includes(userEmail);
                                        if (isAdmin) {
                                            setAllowedEmails(pData.subscription?.allowedEmails || []);
                                            setLinkedTeachers(pData.subscription?.linkedTeachers || []);
                                            setMaxSeats(pData.subscription?.maxSeats || 10);
                                        }
                                    }
                                }, (err) => {
                                    addLog('Principal workspace listener failed (likely not an admin)', { err: err.message });
                                    setAdminEmails([]);
                                });
                            }
                        } else {
                            setLinkedTo(null);
                            setLinkRevoked(false);
                            if (unsubscribePrincipal) {
                                unsubscribePrincipal();
                                unsubscribePrincipal = null;
                            }
                        }

                        setUserExpiry(toDateSafe(data.subscription?.expiry));
                        setTrialClaimedAt(toDateSafe(data.subscription?.trialClaimedAt));
                    } else {
                        addLog('⚠️ WARN: teaching_workspaces doc does not exist yet for this user');
                    }
                }, (err) => {
                    addLog('❌ ERROR: onSnapshot failed', { message: err.message, code: err.code });
                });
            } else {
                addLog('⚠️ Auth state: signed OUT — no user present');
            }
        });

        // Fetch trial days configuration
        const fetchTrialConfig = async () => {
            try {
                const configRef = doc(db, 'system_config', 'independent_settings');
                const configSnap = await getDoc(configRef);
                if (configSnap.exists()) {
                    const data = configSnap.data();
                    if (data.trialDays) setTrialDays(data.trialDays);
                    if (data.defaultMaxSeats) setMaxSeats(data.defaultMaxSeats);
                }
            } catch (e) {
                console.error('Failed to fetch trial config:', e);
            }
        };
        fetchTrialConfig();

        return () => {
            if (unsubscribe) unsubscribe();
            if (unsubscribePrincipal) unsubscribePrincipal();
            if (unsubscribeAuth) unsubscribeAuth();
        };
    }, []);

    const handleInputChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = evt.target;
        setState((prev) => ({ ...prev, [name]: value }));
    }

    const handleInputFocus = (evt: React.FocusEvent<HTMLInputElement>) => {
        setState((prev) => ({ ...prev, focus: evt.target.name }));
    }

    const selectPlanAndProceed = (plan: 'free' | 'individual' | 'school') => {
        setSelectedPlan(plan);
        if (plan === 'free') return;
        setView('payment');
        setMethod('card');
    }

    const handleActivateTrial = async (trialType: 'individual' | 'faculty') => {
        const user = auth.currentUser;
        if (!user) { addLog('❌ ERROR: handleActivateTrial called with no auth user'); return; }

        addLog(`🚀 Activating ${trialType} trial`, { uid: user.uid, trialDays, currentStatus: userStatus });

        if (trialClaimedAt) {
            addLog('⚠️ BLOCKED: Free trial already claimed', { claimedAt: trialClaimedAt.toISOString(), expiry: userExpiry?.toISOString() });
            toast({ title: 'Trial Already Claimed', description: 'Each account can claim the free trial only once, even after it expires. Please choose a paid plan to continue.', variant: 'destructive', duration: 8000 });
            return;
        }
        if (userStatus === 'active') {
            addLog('⚠️ BLOCKED: User already has active paid subscription');
            toast({ title: 'Active Subscription', description: 'You already have an active paid subscription.', variant: 'default', duration: 5000 });
            return;
        }

        setIsActivatingTrial(true);
        try {
            const now = new Date();

            // Guard: block if expiry is in future regardless of status string
            // This prevents overwriting a paid expiry when status hasn't updated yet
            if (userExpiry && userExpiry > now) {
                addLog('⚠️ BLOCKED: User has a future expiry — will not overwrite with trial', { expiry: userExpiry.toISOString() });
                toast({ title: 'Access Active', description: 'Your workspace already has active access until ' + formatSubscriptionDate(userExpiry) + '. No trial needed.', variant: 'default', duration: 5000 });
                return;
            }
            const expiryDate = new Date(now.getTime() + trialDays * 24 * 60 * 60 * 1000);
            const planValue = trialType === 'faculty' ? 'school_trial' : 'individual_trial';

            addLog('✏️ Writing trial to Firestore', {
                path: `teaching_workspaces/${user.uid}`,
                plan: planValue,
                status: 'trial',
                expiry: expiryDate.toISOString()
            });

            const workspaceRef = doc(db, 'teaching_workspaces', user.uid);
            await runTransaction(db, async (transaction) => {
                const workspaceSnap = await transaction.get(workspaceRef);
                const existingClaim = workspaceSnap.exists() ? workspaceSnap.data().subscription?.trialClaimedAt : null;
                if (existingClaim) {
                    throw new Error('Free trial already claimed. Each account can claim the free trial only once, even after it expires.');
                }

                transaction.set(workspaceRef, {
                    ownerId: user.uid,
                    email: user.email || '',
                    subscription: {
                        status: 'trial',
                        plan: planValue,
                        expiry: Timestamp.fromDate(expiryDate),
                        trialClaimedAt: serverTimestamp(),
                    },
                }, { merge: true });
            });
            addLog('✅ Trial activated — onSnapshot will update UI');
            toast({
                title: 'Free Trial Claimed Successfully',
                description: `Your ${trialType === 'faculty' ? 'Faculty' : 'Individual'} free trial has been started and will expire on ${formatSubscriptionDate(expiryDate)}.`,
                variant: 'default',
                duration: 10000,
            });
        } catch (err: any) {
            addLog('❌ ERROR: Trial activation failed', { message: err.message, code: err.code });
            console.error('Trial activation failed:', err);
            toast({ title: 'Trial Activation Failed', description: err.message || 'Failed to activate trial. Please try again.', variant: 'destructive', duration: 6000 });
        } finally {
            setIsActivatingTrial(false);
        }
    };

    const handleMpesaPayment = async () => {
        if (!state.phone) {
            addLog('⚠️ BLOCKED: No phone number entered');
            toast({ title: 'Phone Required', description: 'Please enter a cellular link identifier (phone number).', variant: 'destructive', duration: 5000 });
            return;
        }

        // Resolve dynamic tier variants
        const tier = SUBSCRIPTION_TIERS[activeTier];
        const tierScope = selectedPlan === 'individual' ? tier.variants.individual : tier.variants.faculty;
        const tierVariant = tierScope[billingCycle];

        // Fetch dynamic price from firestore config (pricingState) as primary, fallback to hardcoded tier config
        let amount = tierVariant.price;
        if (activeTier === 'tier1') {
            const firestorePrice = selectedPlan === 'individual' ? pricingState[billingCycle].individual : pricingState[billingCycle].school;
            amount = parseInt(String(firestorePrice).replace(/,/g, ''), 10);
        }

        const planId = tierVariant.id;

        addLog('🚀 M-Pesa STK Push — calling initiateIndependentMpesaPayment', {
            function: 'initiateIndependentMpesaPayment',
            uid: auth.currentUser?.uid ?? 'NOT SIGNED IN',
            phoneNumber: state.phone,
            amount,
            planId,
            functionsRegion: 'us-central1'
        });

        setIsProcessingPayment(true);
        try {
            const initiatePayment = httpsCallable(functions, 'initiateIndependentMpesaPayment');
            const result = await initiatePayment({
                phoneNumber: state.phone,
                amount: amount,
                planId: planId
            });
            const data = result.data as any;
            addLog('✅ STK Push initiated successfully', {
                checkoutRequestId: data?.checkoutRequestId,
                message: data?.message,
                success: data?.success
            });

            if (data?.checkoutRequestId) {
                setPendingTransactionId(data.checkoutRequestId);
                setPaymentStatus('processing');
                setStkPushSent(true);
            }

            toast({ title: 'M-Pesa Request Sent', description: 'Please check your phone for the M-Pesa payment prompt.', variant: 'success', duration: 8000 });
        } catch (err: any) {
            addLog('❌ ERROR: M-Pesa initiation failed', {
                message: err.message,
                code: err.code,
                details: err.details ?? null
            });
            console.error("Payment initiation failed:", err);
            toast({ title: 'M-Pesa Payment Failed', description: err.message, variant: 'destructive', duration: 6000 });
            setPaymentStatus('idle');
            setStkPushSent(false);
        } finally {
            setIsProcessingPayment(false);
        }
    }

    // Track M-Pesa payment status in real-time via onSnapshot on the payment doc
    useEffect(() => {
        if (!pendingTransactionId || paymentStatus !== 'processing') return;

        addLog('📡 Attaching payment listener', { pendingTransactionId });

        const paymentDocRef = doc(db, 'independent_payments', pendingTransactionId);

        const unsubscribe = onSnapshot(paymentDocRef, (snapshot) => {
            if (!snapshot.exists()) return;

            const data = snapshot.data();
            addLog('📡 Payment doc update', { status: data.status, id: pendingTransactionId });

            if (data.status === 'completed') {
                const confirmedExpiry = toDateSafe(data.subscriptionExpiry) || toDateSafe(data.newExpiry);
                setPaymentStatus('completed');
                setPaymentSuccess(true);
                setSuccessBannerDismissed(false);
                setStkPushSent(false);
                setPendingTransactionId(null);
                setState(prev => ({ ...prev, phone: '' }));
                setView('pricing');
                toast({
                    title: "M-Pesa Payment Successful",
                    description: `Your subscription will expire on ${formatSubscriptionDate(confirmedExpiry || userExpiry)}.${data.mpesaReceiptNumber ? ' Receipt: ' + data.mpesaReceiptNumber : ''}`,
                    className: "bg-emerald-950 border-emerald-500 text-emerald-100",
                });
            } else if (data.status === 'failed') {
                setPaymentStatus('failed');
                setStkPushSent(false);
                setPendingTransactionId(null);
                toast({ title: 'Payment Failed', description: data.resultDesc || 'The transaction could not be completed.', variant: 'destructive', duration: 6000 });
            }
        }, (error) => {
            addLog('❌ Payment listener error', { error: error.message });
        });

        // Timeout after 2 minutes — Safaricom usually confirms within 30-60s
        const timeoutId = setTimeout(() => {
            if (paymentStatus === 'processing') {
                addLog('⏱️ Payment listener timed out after 2 minutes');
                setPendingTransactionId(null);
                setPaymentStatus('idle');
                toast({ title: 'Payment Pending', description: 'Payment is taking longer than expected. Your subscription may still activate — please check your plan status in a few moments.', variant: 'default', duration: 10000 });
            }
        }, 120000);

        return () => {
            unsubscribe();
            clearTimeout(timeoutId);
        };
    }, [pendingTransactionId]);

    // Fetch payment history from workspace transactions
    useEffect(() => {
        if (!currentUid) return;

        const fetchTransactions = async () => {
            setLoadingTransactions(true);
            try {
                const [workspaceResult, mpesaResult, pesapalResult] = await Promise.allSettled([
                    getDocs(query(collection(db, `teaching_workspaces/${currentUid}/transactions`), firestoreLimit(5))),
                    getDocs(query(collection(db, 'independent_payments'), where('uid', '==', currentUid), firestoreLimit(5))),
                    getDocs(query(collection(db, 'pesapal_payments'), where('uid', '==', currentUid), firestoreLimit(5))),
                ]);

                const txById = new Map<string, any>();
                const pushTx = (id: string, source: string, data: any) => {
                    const completedDate = toDateSafe(data.completedAt);
                    const createdDate = toDateSafe(data.createdAt);
                    txById.set(id, {
                        id,
                        source,
                        ...data,
                        type: data.type || (source === 'mpesa' ? 'mpesa' : source === 'pesapal' ? 'pesapal' : data.method?.toLowerCase?.()),
                        receiptNumber: data.receiptNumber || data.mpesaReceiptNumber || data.confirmationCode || '',
                        date: completedDate || createdDate || new Date(0),
                    });
                };

                if (workspaceResult.status === 'fulfilled') {
                    workspaceResult.value.forEach((txDoc) => pushTx(txDoc.id, 'workspace', txDoc.data()));
                } else {
                    addLog('⚠️ Workspace transactions unavailable', { error: workspaceResult.reason?.message });
                }

                if (mpesaResult.status === 'fulfilled') {
                    mpesaResult.value.forEach((txDoc) => pushTx(txDoc.id, 'mpesa', txDoc.data()));
                } else {
                    addLog('⚠️ M-Pesa payment attempts unavailable', { error: mpesaResult.reason?.message });
                }

                if (pesapalResult.status === 'fulfilled') {
                    pesapalResult.value.forEach((txDoc) => pushTx(txDoc.id, 'pesapal', txDoc.data()));
                } else {
                    addLog('⚠️ PesaPal payment attempts unavailable', { error: pesapalResult.reason?.message });
                }

                const txList = Array.from(txById.values()).sort((a, b) => b.date.getTime() - a.date.getTime()).slice(0, 5);
                setTransactions(txList);
                addLog(`📋 Loaded ${txList.length} payment records`);
            } catch (err: any) {
                addLog('⚠️ Failed to load transactions', { error: err.message });
            } finally {
                setLoadingTransactions(false);
            }
        };

        fetchTransactions();
    }, [currentUid]);

    // Single Source of Truth: We strictly rely on the Firestore document's `plan` field.
    // We removed the transaction-history override because it caused race conditions
    // where users would be temporarily downgraded or upgraded if their payment history
    // conflicted with the admin/webhook's final decision in Firestore.
    const effectivePlan = userPlan;

    // True only when the user has a paid & active school/faculty subscription.
    // school_trial users have faculty ACCESS but are NOT owners for management purposes
    // — they still see the sync panel (they are trialing it) but this flag controls the
    // header button, status banners, and pricing card CTA.
    const isFacultyOwner = isFacultyPlanScope(effectivePlan) && userStatus === 'active' && !isTrialPlan(effectivePlan);
    // isFacultyTrialing: on faculty trial — can see sync panel but needs to pay to keep it.
    const isFacultyTrialing = effectivePlan === 'school_trial' && userStatus === 'trial';
    // Admins linked to a principal's workspace (not an owner themselves).
    const isFacultyAdmin = auth.currentUser?.email ? adminEmails.includes(auth.currentUser.email) : false;
    const isFacultyPrincipal = isFacultyOwner || (!!linkedTo && isFacultyAdmin);

    // Keep the success banner visible until the user dismisses it.
    useEffect(() => {
        if (paymentSuccess) setSuccessBannerDismissed(false);
    }, [paymentSuccess, userExpiry]);

    // Reset STK indicator when leaving payment view
    useEffect(() => {
        if (view !== 'payment') {
            setStkPushSent(false);
        }
    }, [view]);

    const handleJoinFaculty = async () => {
        if (!facultyCode) {
            addLog('⚠️ BLOCKED: No faculty code entered');
            toast({ title: 'Faculty Code Required', description: 'Please provide a valid Faculty Code.', variant: 'destructive', duration: 5000 });
            return;
        }

        addLog('🔗 Joining faculty — calling joinFacultySubscription', {
            function: 'joinFacultySubscription',
            facultyCode,
            callerUid: auth.currentUser?.uid ?? 'NOT SIGNED IN'
        });

        setIsJoiningFaculty(true);
        try {
            const joinFaculty = httpsCallable(functions, 'joinFacultySubscription');
            const result = await joinFaculty({ facultyCode });
            const data = result.data as any;
            addLog('✅ Faculty join successful', { message: data?.message, success: data?.success });
            toast({ title: 'Faculty Linked', description: data?.message || 'Successfully linked to faculty!', variant: 'success', duration: 6000 });
            setFacultyCode('');
        } catch (err: any) {
            addLog('❌ ERROR: joinFacultySubscription failed', {
                message: err.message,
                code: err.code,
                details: err.details ?? null
            });
            console.error("Join faculty failed:", err);
            toast({ title: 'Link Failed', description: err.message, variant: 'destructive', duration: 6000 });
        } finally {
            setIsJoiningFaculty(false);
        }
    }

    const handleCopyFacultyCode = () => {
        if (auth.currentUser?.uid) {
            navigator.clipboard.writeText(auth.currentUser.uid);
            setCopiedFacultyCode(true);
            setTimeout(() => setCopiedFacultyCode(false), 2000);
        }
    };

    const handleRevokeAccess = async (teacher: { uid: string; email: string; displayName?: string; joinedAt: any }) => {
        const user = auth.currentUser;
        if (!user) return;
        if (!window.confirm(`Revoke access for ${teacher.email}? They will be notified and lose access immediately.`)) return;

        setIsRevokingAccess(teacher.uid);
        try {
            const targetUid = linkedTo?.principalUid || user.uid;
            // 1. Remove from principal's linkedTeachers list
            const principalRef = doc(db, 'teaching_workspaces', targetUid);
            await updateDoc(principalRef, {
                'subscription.linkedTeachers': arrayRemove(teacher)
            });
            // 2. Mark revoked on teacher's own doc so they see a notification
            //    (Cloud function handles this cross-user write securely)
            const revokeAccess = httpsCallable(functions, 'revokeFacultyAccess');
            await revokeAccess({ targetUid: teacher.uid });
        } catch (err: any) {
            // Fallback: if cloud function unavailable, still remove from principal's list
            console.error('Revoke error:', err);
            toast({ title: 'Access Revoked', description: 'Access revoked from your list. The teacher may need to refresh to see the change.', variant: 'success', duration: 6000 });
        } finally {
            setIsRevokingAccess(null);
        }
    };

    const handleAddEmail = async () => {
        const user = auth.currentUser;
        if (!user || !newEmail || !newEmail.includes('@')) return;
        if (allowedEmails.length >= maxSeats) {
            toast({ title: 'Seat Limit Reached', description: `Your Faculty Plan supports a maximum of ${maxSeats} faculty members. Contact support to expand your seats.`, variant: 'destructive', duration: 8000 });
            return;
        }
        try {
            const targetUid = linkedTo?.principalUid || user.uid;
            await updateDoc(doc(db, 'teaching_workspaces', targetUid), {
                'subscription.allowedEmails': arrayUnion(newEmail.trim().toLowerCase())
            });
            setNewEmail('');
        } catch (e) {
            console.error("Failed to add email:", e);
        }
    };

    const handleRemoveEmail = async (emailToRemove: string) => {
        const user = auth.currentUser;
        if (!user) return;
        const mainAccountEmail = linkedTo?.principalEmail || user.email;
        if (emailToRemove === mainAccountEmail) {
             toast({ title: 'Action Blocked', description: 'Cannot remove the main subscription owner.', variant: 'destructive', duration: 4000 });
             return;
        }
        try {
            const targetUid = linkedTo?.principalUid || user.uid;
            await updateDoc(doc(db, 'teaching_workspaces', targetUid), {
                'subscription.allowedEmails': arrayRemove(emailToRemove),
                'subscription.adminEmails': arrayRemove(emailToRemove)
            });
        } catch (e) {
            console.error("Failed to remove email:", e);
        }
    };

    const executeToggleAdmin = async (emailToToggle: string, isAdmin: boolean) => {
        const user = auth.currentUser;
        if (!user) return;
        const targetUid = linkedTo?.principalUid || user.uid;
        
        try {
            await updateDoc(doc(db, 'teaching_workspaces', targetUid), {
                'subscription.adminEmails': isAdmin ? arrayRemove(emailToToggle) : arrayUnion(emailToToggle)
            });
            toast({
                title: 'Success',
                description: `Admin privileges have been ${isAdmin ? 'revoked' : 'granted'} for ${emailToToggle}.`,
                variant: 'success'
            });
        } catch (e) {
            console.error("Failed to toggle admin status:", e);
        }
    };

    const handleToggleAdmin = (emailToToggle: string) => {
        console.log('[ADMIN-BTN] Button clicked for:', emailToToggle);
        const user = auth.currentUser;
        console.log('[ADMIN-BTN] auth.currentUser:', user?.email ?? 'NULL — not logged in');
        if (!user) { console.log('[ADMIN-BTN] BLOCKED: no user, returning early'); return; }
        const isAdmin = adminEmails.includes(emailToToggle);
        const mainAccountEmail = linkedTo?.principalEmail || user.email;
        console.log('[ADMIN-BTN] state:', { adminEmails, isAdmin, mainAccountEmail, linkedTo, emailToToggle });
        if (emailToToggle === mainAccountEmail && isAdmin) {
            console.log('[ADMIN-BTN] BLOCKED: trying to remove owner admin rights');
            toast({ title: 'Action Blocked', description: 'Cannot remove admin rights from the main subscription owner.', variant: 'destructive', duration: 4000 });
            return;
        }
        console.log('[ADMIN-BTN] calling setAdminConfirmDialog → open:true');
        setAdminConfirmDialog({ open: true, email: emailToToggle, isCurrentlyAdmin: isAdmin });
        console.log('[ADMIN-BTN] setAdminConfirmDialog called');
    };

    // Detect PesaPal redirect callback: ?pesapal_callback=1
    // SECURITY: Only dismiss the iframe here — do NOT mark payment as successful from URL param alone.
    // Actual success is confirmed exclusively by the Firestore onSnapshot detecting status='active'.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('pesapal_callback') === '1') {
            window.history.replaceState({}, '', window.location.pathname);
            setPesapalIframeUrl(null);
            setPesapalOrderId(null);
            setView('pricing');
            addLog('📡 PesaPal callback URL detected — waiting for Firestore to confirm subscription activation...');
            toast({
                title: "Payment Received",
                description: "Verifying your payment. Your subscription will activate in a moment.",
                className: "bg-blue-950 border-blue-500 text-blue-100",
            });
        }

        const handleMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;
            if (event.data === 'pesapal_payment_complete') {
                setPesapalIframeUrl(null);
                setPesapalOrderId(null);
                setView('pricing');
                addLog('📡 PesaPal postMessage received — waiting for Firestore confirmation...');
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, []);

    // Auto-close iframe and show success dialog when PesaPal subscription activates via Firestore
    useEffect(() => {
        if (userStatus === 'active' && pesapalIframeUrl) {
            setPesapalIframeUrl(null);
            setPesapalOrderId(null);
            setView('pricing');
            setPaymentConfirmDialog({
                open: true,
                plan: userPlan,
                expiry: userExpiry,
                method: 'PesaPal',
            });
        }
    }, [userStatus]);

    // Auto-redirect to pricing when M-Pesa payment completes and subscription activates
    useEffect(() => {
        if (userStatus === 'active' && paymentStatus === 'processing') {
            addLog('✅ Subscription activated — M-Pesa payment completed');
            setPaymentStatus('completed');
            setPaymentSuccess(true);
            setSuccessBannerDismissed(false);
            setStkPushSent(false);
            setPendingTransactionId(null);
            setState(prev => ({ ...prev, phone: '' }));
            setView('pricing');
            setPaymentConfirmDialog({
                open: true,
                plan: userPlan,
                expiry: userExpiry,
                method: 'M-Pesa',
            });
        }
    }, [userStatus, paymentStatus]);

    const handlePesapalPayment = async () => {
        if (!cardEmail && !cardPhone) {
            addLog('⚠️ BLOCKED: No email or phone provided for PesaPal');
            toast({ title: 'Billing Details Required', description: 'Please enter at least an email or phone number for billing.', variant: 'destructive', duration: 5000 });
            return;
        }

        const planId = `${selectedPlan}_${billingCycle}`;

        addLog('🚀 PesaPal — calling initiatePesapalPayment', {
            function: 'initiatePesapalPayment',
            uid: auth.currentUser?.uid ?? 'NOT SIGNED IN',
            planId,
            firstName: cardFirstName || '(empty)',
            lastName: cardLastName || '(empty)',
            email: cardEmail || '(empty)',
            phoneNumber: cardPhone || '(empty)',
            note: 'Amount resolved server-side from systemConfig/independentPricing'
        });

        setIsProcessingPayment(true);
        try {
            const initiate = httpsCallable(functions, 'initiatePesapalPayment');
            const result: any = await initiate({
                planId,
                firstName: cardFirstName,
                lastName: cardLastName,
                email: cardEmail,
                phoneNumber: cardPhone,
            });

            addLog('📦 PesaPal raw response', result.data);

            if (result.data?.redirectUrl) {
                addLog('✅ PesaPal redirect URL received', {
                    orderTrackingId: result.data.orderTrackingId,
                    redirectUrl: result.data.redirectUrl
                });
                setPesapalIframeUrl(result.data.redirectUrl);
                setPesapalOrderId(result.data.orderTrackingId);
            } else {
                throw new Error('No redirect URL returned from payment gateway.');
            }
        } catch (err: any) {
            addLog('❌ ERROR: initiatePesapalPayment failed', {
                message: err.message,
                code: err.code,
                details: err.details ?? null
            });
            console.error('PesaPal initiation failed:', err);
            toast({ title: 'Payment Initiation Failed', description: err.message, variant: 'destructive', duration: 6000 });
        } finally {
            setIsProcessingPayment(false);
        }
    };

    if (view === 'pricing') {
        if (loadingPricing) {
            return <WorkspaceLoader minHeight="min-h-screen" />;
        }

        const hasExplicitTrial = !!trialClaimedAt;
        const isTrialExpired = userExpiry ? userExpiry < new Date() : false;
        const hasEverPaid = transactions.some(tx => tx.status === 'completed' && tx.type !== 'free_trial');
        // Block trial if user has any future expiry (covers paid-but-status-not-updated edge case)
        const hasFutureExpiry = userExpiry !== null && userExpiry > new Date();
        const hasActivePaidSubscription = userStatus === 'active' && hasFutureExpiry;
        const canClaimTrial = !hasExplicitTrial && !hasActivePaidSubscription && !hasFutureExpiry && !hasEverPaid;

        if (showFacultyPanel) {
            return (
                <>
                <div className="w-full bg-slate-50 dark:bg-[#020617] text-slate-900 dark:text-zinc-100 py-4 px-3 sm:py-6 sm:px-4 font-sans selection:bg-blue-500/30">
                    <div className="max-w-[1000px] mx-auto">
                        <button
                            onClick={() => setShowFacultyPanel(false)}
                            className="flex items-center gap-2 text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white mb-6 uppercase tracking-[0.2em] text-[10px] font-black transition-all"
                        >
                            <ArrowLeft className="w-3 h-3" /> Back to Billing
                        </button>

                        {/* ── Faculty Panel Branded Header ── */}
                        {(isFacultyOwner || isFacultyTrialing) && institutionName && (
                            <div className="mb-6 border-b border-slate-200 dark:border-white/10 pb-6">
                                <p className="text-[9px] font-black uppercase tracking-[0.25em] text-orange-500/70 mb-1">Faculty Management</p>
                                <h1 className="font-serif italic lowercase tracking-tight text-4xl sm:text-5xl"
                                    style={{ color: 'rgb(249,115,22)', textShadow: '0 0 40px rgba(249,115,22,0.2)' }}>
                                    {institutionName}
                                </h1>
                                <p className="text-[9px] text-slate-500 dark:text-slate-500 tracking-widest mt-1 font-mono">{organizationId}</p>
                            </div>
                        )}

                        {/* ── Upgrade Gate: blocks individual plan/trial users who manually navigate here ── */}
                        {!isFacultyOwner && !isFacultyTrialing && !linkedTo && (
                            <div className="flex flex-col items-center justify-center py-20 gap-6 text-center">
                                <div className="w-16 h-16 bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mx-auto">
                                    <Building2 className="w-7 h-7 text-amber-500" />
                                </div>
                                <div className="space-y-2 max-w-md">
                                    <p className="text-[10px] font-black uppercase tracking-[0.3em] text-amber-500">Faculty Plan Required</p>
                                    <h2 className="text-lg font-black uppercase tracking-tight text-slate-900 dark:text-white">Upgrade to Manage Faculty</h2>
                                    <p className="text-[10px] text-slate-500 dark:text-slate-400 tracking-wider leading-relaxed">
                                        Faculty management — including seat allocation, email allowlisting, and teacher synchronization — is exclusive to the Faculty Plan.
                                        Your current plan is <span className="font-black text-slate-700 dark:text-zinc-200">{
                                            effectivePlan === 'individual_trial' ? 'Individual Trial' :
                                            effectivePlan.includes('individual') ? 'Individual' :
                                            effectivePlan || 'None'
                                        }</span>. Upgrade to a Faculty Plan to unlock these tools.
                                    </p>
                                </div>
                                <button
                                    onClick={() => setShowFacultyPanel(false)}
                                    className="h-10 px-8 bg-blue-600 hover:bg-blue-500 text-white text-[9px] font-black uppercase tracking-widest transition-all active:scale-[0.98]"
                                >
                                    View Plans &amp; Upgrade
                                </button>
                            </div>
                        )}

                        <div className="space-y-6">

                            {/* ── Missing Institution Name Banner ── */}
                            {(isFacultyOwner || isFacultyTrialing) && !organizationId && (
                                <div className="border border-red-500/40 bg-red-950/20 p-4 flex items-start gap-3 shadow-lg relative overflow-hidden">
                                    <div className="absolute top-0 left-0 w-1 h-full bg-red-500/60" />
                                    <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-[11px] font-black uppercase tracking-widest text-red-500 mb-1">Mandatory Setup Required</p>
                                        <p className="text-[10px] text-red-400/90 tracking-wider leading-relaxed">
                                            You must set your <strong>Institution Name</strong> in the Organization Profile below. Setting a name is mandatory to maintain your faculty tools. Once set, you will be assigned a secure Faculty ID.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* ── Faculty Trial Banner: user is trialing faculty, remind them to upgrade ── */}
                            {isFacultyTrialing && (
                                <div className="border border-amber-500/40 bg-amber-950/20 p-4 flex items-start gap-3">
                                    <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-[10px] font-black uppercase tracking-widest text-amber-400 mb-1">Faculty Trial Active — Upgrade to Keep Access</p>
                                        <p className="text-[9px] text-slate-400 tracking-wider leading-relaxed">
                                            You are currently trialing the Faculty Plan. These tools are available during your {userExpiry ? `trial period (expires ${userExpiry.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })})` : 'trial period'}.
                                            After your trial ends, purchase a Faculty Plan to continue managing your teaching staff.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* ── Faculty Member Status Banner (for teachers who joined via code) ── */}
                            {linkedTo && !isFacultyOwner && (
                                <div className={`border p-3 sm:p-4 relative ${
                                    linkRevoked
                                        ? 'bg-red-950/30 border-red-500/40'
                                        : 'bg-emerald-950/20 border-emerald-500/20'
                                }`}>
                                    <div className={`absolute top-0 left-0 w-1 h-full ${linkRevoked ? 'bg-red-500/60' : 'bg-emerald-500/40'}`} />
                                    <div className="flex items-start gap-3 pl-2">
                                        {linkRevoked
                                            ? <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
                                            : <Info className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                                        }
                                        <div className="flex-1 min-w-0">
                                            {linkRevoked ? (
                                                <>
                                                    <p className="text-[10px] font-black uppercase tracking-widest text-red-500 dark:text-red-400 mb-1">⚠ Faculty Access Revoked</p>
                                                    <p className="text-[9px] text-slate-500 dark:text-slate-400 tracking-wider leading-relaxed">
                                                        Your faculty access has been revoked by the subscription holder. You no longer have access to premium features.
                                                        Contact your principal to be re-added, or upgrade to your own plan.
                                                    </p>
                                                </>
                                            ) : (
                                                <>
                                                    <p className="text-[10px] font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400 mb-2">✓ Linked Faculty Access Active</p>
                                                    {linkedTo.institutionName && (
                                                        <p className="text-base font-black uppercase tracking-tight text-slate-900 dark:text-white mb-1">{linkedTo.institutionName}</p>
                                                    )}
                                                    <p className="text-[9px] text-slate-500 dark:text-slate-400 tracking-wider leading-relaxed mb-2">
                                                        You are accessing premium features through this institution's shared faculty subscription.
                                                        {linkedTo.principalEmail && (
                                                            <> Account owner: <span className="font-mono text-slate-700 dark:text-zinc-300">{linkedTo.principalEmail}</span>.</>
                                                        )}{' '}
                                                        If your principal revokes access, you will see a warning here immediately.
                                                    </p>
                                                    {linkedTo.organizationId && (
                                                        <div className="mt-2">
                                                            <p className="text-[8px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-500 mb-1">Organization ID</p>
                                                            <button
                                                                onClick={() => {
                                                                    navigator.clipboard.writeText(linkedTo.organizationId || '');
                                                                    setCopiedOrgId(true);
                                                                    setTimeout(() => setCopiedOrgId(false), 2000);
                                                                }}
                                                                className="h-8 inline-flex items-center gap-2 px-3 border border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10 font-mono text-[11px] text-emerald-600 dark:text-emerald-400 tracking-[0.15em] transition-colors cursor-pointer"
                                                            >
                                                                <span>{linkedTo.organizationId}</span>
                                                                {copiedOrgId
                                                                    ? <span className="flex items-center gap-1 text-[8px] font-black uppercase text-emerald-500"><Check className="w-3 h-3" /> Copied!</span>
                                                                    : <Copy className="w-3 h-3" />
                                                                }
                                                            </button>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ── Organization Profile (for owners) ── */}
                            {(isFacultyOwner || isFacultyTrialing) && (
                                <div className="bg-slate-100 dark:bg-[#0a1024] border border-blue-500/30 p-4 sm:p-6 text-left shadow-2xl relative overflow-hidden group mb-6">
                                    <div className="absolute top-0 left-0 w-1 h-full bg-blue-500/50" />

                                    <div className="flex items-center justify-between mb-3">
                                        <h3 className="text-[12px] font-black uppercase tracking-[0.2em] text-blue-600 dark:text-blue-400 flex items-center gap-2">
                                            <Building2 className="w-4 h-4" />
                                            Organization Profile
                                        </h3>
                                        {/* 3-dot kebab menu */}
                                        <div className="relative">
                                            <button
                                                onClick={() => setOrgMenuOpen(p => !p)}
                                                className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-white/10 transition-colors"
                                            >
                                                <MoreVertical className="w-4 h-4" />
                                            </button>
                                            {orgMenuOpen && (
                                                <div className="absolute right-0 top-8 z-20 w-44 bg-white dark:bg-[#0f1729] border border-slate-200 dark:border-white/10 shadow-xl">
                                                    <button
                                                        onClick={() => {
                                                            setEditOrgNameValue(institutionName);
                                                            setIsEditingOrgName(true);
                                                            setOrgMenuOpen(false);
                                                        }}
                                                        className="w-full flex items-center gap-2 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
                                                    >
                                                        <Pencil className="w-3 h-3" /> Edit Name
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Read-only display */}
                                    {!isEditingOrgName ? (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <Label className="text-[8px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-2 block">Institution Name</Label>
                                                <div className="h-10 border border-slate-200 dark:border-white/10 bg-slate-100/50 dark:bg-white/5 flex items-center px-4 text-[12px] font-black text-slate-900 dark:text-zinc-100 tracking-wide">
                                                    {institutionName}
                                                </div>
                                            </div>
                                            <div>
                                                <Label className="text-[8px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-2 block">Organization ID</Label>
                                                <button
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(organizationId);
                                                        setCopiedOrgId(true);
                                                        setTimeout(() => setCopiedOrgId(false), 2000);
                                                    }}
                                                    className="h-10 w-full border border-slate-200 dark:border-white/10 bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 flex items-center justify-between px-4 font-mono text-[11px] text-slate-500 dark:text-slate-400 tracking-[0.15em] transition-colors cursor-pointer group/id"
                                                >
                                                    <span>{organizationId}</span>
                                                    {copiedOrgId
                                                        ? <span className="flex items-center gap-1 text-[8px] font-black uppercase text-emerald-500 shrink-0"><Check className="w-3 h-3" /> Copied!</span>
                                                        : <Copy className="w-3 h-3 text-slate-400 group-hover/id:text-slate-900 dark:group-hover/id:text-white transition-colors shrink-0" />
                                                    }
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        /* Inline edit mode */
                                        <div className="space-y-3">
                                            <div>
                                                <Label className="text-[8px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-2 block">New Institution Name</Label>
                                                <Input
                                                    value={editOrgNameValue}
                                                    onChange={(e) => setEditOrgNameValue(e.target.value)}
                                                    placeholder="e.g. Sonoma High School"
                                                    className="h-10 text-[12px] font-medium bg-white dark:bg-black border-slate-300 dark:border-white/20"
                                                    autoFocus
                                                />
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={handleUpdateOrgName}
                                                    disabled={isSavingOrgDetails || !editOrgNameValue.trim()}
                                                    className="h-9 px-5 bg-blue-600 hover:bg-blue-500 text-white text-[9px] font-black uppercase tracking-widest transition-all disabled:opacity-50 flex items-center gap-2"
                                                >
                                                    {isSavingOrgDetails ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                                    Save
                                                </button>
                                                <button
                                                    onClick={() => setIsEditingOrgName(false)}
                                                    className="h-9 px-5 border border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 text-[9px] font-black uppercase tracking-widest transition-all"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ── Faculty Synchronization Panel (for owners AND faculty-trial users) ── */}
                            {(isFacultyOwner || isFacultyTrialing) && (
                                <div className="bg-slate-100 dark:bg-[#0a1024] border border-blue-500/30 p-4 sm:p-6 text-left shadow-2xl relative overflow-hidden group">
                                    <div className="absolute top-0 left-0 w-1 h-full bg-blue-500/50" />
                                    <h3 className="text-[12px] font-black uppercase tracking-[0.2em] text-blue-600 dark:text-blue-400 mb-3">Faculty Synchronization Panel</h3>

                                    <div className="bg-blue-500/5 border border-blue-500/20 px-3 sm:px-4 py-3 mb-5">
                                        <p className="text-[8px] sm:text-[9px] text-slate-500 dark:text-slate-400 leading-relaxed tracking-wider">
                                            <span className="text-blue-600 dark:text-blue-400 font-black">HOW TO INVITE TEACHERS —</span>{' '}
                                            <span className="text-slate-600 dark:text-slate-300">Step 1:</span> Add each teacher's email to the Authorized Accounts list.{' '}
                                            <span className="text-slate-600 dark:text-slate-300">Step 2:</span> Copy your Faculty Code below and share it with your teachers.{' '}
                                            <span className="text-slate-600 dark:text-slate-300">Step 3:</span> Teachers paste the code in the "Join Existing Faculty" section below.
                                            Once linked, they appear in the Active Linked Teachers tracker and you can revoke access at any time.
                                        </p>
                                    </div>

                                    <div className="mb-6">
                                        <Label className="text-[8px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Your Secure Faculty Code — Click to Copy</Label>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <div
                                                    onClick={handleCopyFacultyCode}
                                                    className="h-10 border border-slate-200 dark:border-white/10 bg-slate-100 dark:bg-white/5 flex items-center justify-between px-4 font-mono text-[11px] text-slate-700 dark:text-zinc-300 tracking-[0.15em] mt-1 shadow-inner cursor-pointer hover:bg-slate-200 dark:hover:bg-white/10 transition-colors group/copy"
                                                >
                                                    <span className="truncate mr-2">{auth.currentUser?.uid}</span>
                                                    {copiedFacultyCode ? (
                                                        <span className="flex items-center gap-1 text-[8px] font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400 shrink-0">
                                                            <Check className="w-3 h-3" /> Copied!
                                                        </span>
                                                    ) : (
                                                        <Copy className="w-3.5 h-3.5 text-slate-600 dark:text-slate-500 group-hover/copy:text-slate-900 dark:group-hover/copy:text-white transition-colors shrink-0" />
                                                    )}
                                                </div>
                                            </TooltipTrigger>
                                            <BillingTooltipContent side="top" className="max-w-[280px]">
                                                <p className="text-[10px] leading-relaxed">This is your unique faculty code. Share it with teachers whose emails are allowlisted so they can join your faculty subscription.</p>
                                            </BillingTooltipContent>
                                        </Tooltip>
                                        <p className="text-[8px] text-slate-600 dark:text-slate-500 mt-1.5 tracking-wider">Only users whose email is allowlisted AND who enter this exact code will be synchronized.</p>
                                    </div>

                                    <div className="border-t border-slate-200 dark:border-white/10 pt-5">
                                        <div className="flex justify-between items-center mb-1">
                                            <div className="flex items-center gap-2">
                                                <div className="w-6 h-6 bg-blue-600/10 rounded-none flex items-center justify-center">
                                                    <span className="text-blue-600 dark:text-blue-400 font-black">1</span>
                                                </div>
                                                <Label className="text-[9px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Authorized Email Allowlist</Label>
                                            </div>
                                            <span className={`text-[8px] font-black uppercase tracking-widest ${allowedEmails.length >= maxSeats ? 'text-red-500 dark:text-red-400' : 'text-blue-600 dark:text-blue-400'}`}>
                                                {allowedEmails.length} / {maxSeats}
                                            </span>
                                        </div>
                                        <p className="text-[8px] text-slate-600 dark:text-slate-500 tracking-wider mb-3 ml-8">Only allowlisted emails can join your faculty workspace.</p>

                                        {/* Seats progress bar */}
                                        <div className="ml-8 mb-4">
                                            <div className="h-1 bg-slate-200 dark:bg-white/5 w-full overflow-hidden">
                                                <div
                                                    className="h-full bg-blue-500 transition-all duration-300"
                                                    style={{ width: `${maxSeats > 0 ? (allowedEmails.length / maxSeats) * 100 : 0}%` }}
                                                />
                                            </div>
                                        </div>

                                        {/* Add email input */}
                                        <div className="ml-8 flex gap-2 mb-4">
                                            <div className="flex-1 relative">
                                                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 dark:text-slate-400" />
                                                <Input
                                                    placeholder="teacher@school.com"
                                                    type="email"
                                                    value={newEmail}
                                                    onChange={(e) => setNewEmail(e.target.value)}
                                                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddEmail(); }}
                                                    className="h-10 pl-9 rounded-none border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 text-[11px] font-mono tracking-wide text-slate-900 dark:text-zinc-200 placeholder:text-zinc-500 focus:border-blue-500 transition-colors"
                                                />
                                            </div>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <button
                                                        onClick={handleAddEmail}
                                                        disabled={!newEmail || allowedEmails.length >= maxSeats}
                                                        className="h-10 px-5 bg-blue-600 hover:bg-blue-500 text-white text-[9px] font-black uppercase tracking-widest transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5 shrink-0"
                                                    >
                                                        <span className="text-sm leading-none">+</span> Add
                                                    </button>
                                                </TooltipTrigger>
                                                <BillingTooltipContent side="top">
                                                    <p className="text-[10px]">Add this email to the allowlist. Teacher must also enter your faculty code to join.</p>
                                                </BillingTooltipContent>
                                            </Tooltip>
                                        </div>

                                        {/* Email list */}
                                        <div className="ml-8 bg-white dark:bg-white/[0.02] border border-slate-200/80 dark:border-white/5 overflow-hidden mb-6">
                                            {allowedEmails.length === 0 ? (
                                                <div className="py-10 text-center">
                                                    <div className="w-10 h-10 bg-slate-100 dark:bg-white/5 rounded-none flex items-center justify-center mx-auto mb-3">
                                                        <User className="w-4 h-4 text-slate-400 dark:text-slate-600" />
                                                    </div>
                                                    <p className="text-[10px] font-bold text-slate-500 dark:text-slate-500 mb-1">No teachers added yet</p>
                                                    <p className="text-[8px] text-slate-500 dark:text-slate-600">Enter an email above and click Add to authorize a teacher.</p>
                                                </div>
                                            ) : (
                                                <ul className="divide-y divide-slate-100 dark:divide-white/5">
                                                    {allowedEmails.map((email, idx) => {
                                                        const isAdmin = adminEmails.includes(email);
                                                        const isMainAccount = email === (linkedTo?.principalEmail || auth.currentUser?.email);
                                                        return (
                                                        <li key={email} className="px-4 py-3 flex items-center justify-between group/item hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors">
                                                            <div className="flex items-center gap-3">
                                                                <div className={`w-7 h-7 ${isAdmin ? 'bg-indigo-600/10' : 'bg-blue-600/10'} rounded-none flex items-center justify-center shrink-0`}>
                                                                    <span className={`text-[9px] font-black ${isAdmin ? 'text-indigo-500' : 'text-blue-500'}`}>{email.charAt(0).toUpperCase()}</span>
                                                                </div>
                                                                <div className="flex flex-col">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-[11px] font-mono text-slate-800 dark:text-zinc-200">{email}</span>
                                                                        {isAdmin && <Badge className="bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-500/20 border-none px-1.5 py-0 h-4 text-[7px] uppercase tracking-widest font-black rounded-sm">Admin</Badge>}
                                                                        {isMainAccount && <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20 border-none px-1.5 py-0 h-4 text-[7px] uppercase tracking-widest font-black rounded-sm">Owner</Badge>}
                                                                    </div>
                                                                    <span className="text-[8px] text-slate-500 dark:text-slate-600 uppercase tracking-widest">Seat {idx + 1}</span>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                {!isMainAccount && (
                                                                    <button
                                                                        onClick={() => handleToggleAdmin(email)}
                                                                        className={`flex items-center gap-1 text-[8px] font-bold uppercase tracking-widest px-2 py-1 transition-all ${isAdmin ? 'text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-950/20' : 'text-slate-400 hover:text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-950/20'}`}
                                                                    >
                                                                        <ShieldCheck className="w-3 h-3" /> {isAdmin ? 'Revoke Admin Privileges' : 'Give Admin Privileges'}
                                                                    </button>
                                                                )}
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <button
                                                                            onClick={() => handleRemoveEmail(email)}
                                                                            disabled={isMainAccount}
                                                                            className="flex items-center gap-1 text-[8px] font-bold uppercase tracking-widest text-slate-400 hover:text-red-400 transition-all px-2 py-1 hover:bg-red-50 dark:hover:bg-red-950/20 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 disabled:cursor-not-allowed"
                                                                        >
                                                                            <X className="w-3 h-3" /> Remove
                                                                        </button>
                                                                    </TooltipTrigger>
                                                                    <BillingTooltipContent side="left">
                                                                        <p className="text-[10px]">{isMainAccount ? 'Cannot remove the main account' : 'Remove this email from the allowlist.'}</p>
                                                                    </BillingTooltipContent>
                                                                </Tooltip>
                                                            </div>
                                                        </li>
                                                    )})}
                                                </ul>
                                            )}
                                        </div>

                                        <div className="border-t border-slate-200 dark:border-white/10 pt-5">
                                            <div className="flex items-center justify-between mb-1">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-6 h-6 bg-emerald-600/10 rounded-none flex items-center justify-center">
                                                        <span className="text-[9px] font-black text-emerald-600 dark:text-emerald-400">2</span>
                                                    </div>
                                                    <Label className="text-[9px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Active Linked Teachers</Label>
                                                </div>
                                                <span className="text-[8px] font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400">{linkedTeachers.length} Active</span>
                                            </div>
                                            <p className="text-[8px] text-slate-600 dark:text-slate-500 tracking-wider mb-4 ml-8">Teachers who joined with your faculty code. Revoke access at any time.</p>

                                            <div className="ml-8 bg-white dark:bg-white/[0.02] border border-slate-200/80 dark:border-white/5 overflow-hidden">
                                                {linkedTeachers.length === 0 ? (
                                                    <div className="py-10 text-center">
                                                        <div className="w-10 h-10 bg-slate-100 dark:bg-white/5 rounded-none flex items-center justify-center mx-auto mb-3">
                                                            <Users className="w-4 h-4 text-slate-400 dark:text-slate-600" />
                                                        </div>
                                                        <p className="text-[10px] font-bold text-slate-500 dark:text-slate-500 mb-1">No linked teachers yet</p>
                                                        <p className="text-[8px] text-slate-500 dark:text-slate-600">Share your faculty code. Teachers who join will appear here.</p>
                                                    </div>
                                                ) : (
                                                    <ul className="divide-y divide-slate-100 dark:divide-white/5">
                                                        {linkedTeachers.map((teacher) => (
                                                            <li key={teacher.uid} className="px-4 py-3 flex items-center justify-between group/row hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors">
                                                                <div className="flex items-center gap-3">
                                                                    <div className="w-7 h-7 bg-emerald-500/10 rounded-none flex items-center justify-center shrink-0">
                                                                        <span className="text-[9px] font-black text-emerald-500">{teacher.email.charAt(0).toUpperCase()}</span>
                                                                    </div>
                                                                    <div className="flex flex-col gap-0.5">
                                                                        <span className="text-[11px] font-mono text-slate-800 dark:text-zinc-200">{teacher.email}</span>
                                                                        <div className="flex items-center gap-2">
                                                                            {teacher.displayName && (
                                                                                <span className="text-[8px] text-slate-500 dark:text-slate-400">{teacher.displayName}</span>
                                                                            )}
                                                                            <span className="text-[8px] text-slate-500 dark:text-slate-600">
                                                                                Joined {teacher.joinedAt?.toDate
                                                                                    ? teacher.joinedAt.toDate().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                                                                                    : '—'}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <button
                                                                            onClick={() => handleRevokeAccess(teacher)}
                                                                            disabled={isRevokingAccess === teacher.uid}
                                                                            className="flex items-center gap-1.5 text-[8px] font-bold uppercase tracking-widest text-slate-400 hover:text-red-400 opacity-0 group-hover/row:opacity-100 transition-all px-2 py-1 hover:bg-red-50 dark:hover:bg-red-950/20 disabled:opacity-50"
                                                                        >
                                                                            {isRevokingAccess === teacher.uid
                                                                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                                                                : <Link2Off className="w-3 h-3" />
                                                                            }
                                                                            Revoke
                                                                        </button>
                                                                    </TooltipTrigger>
                                                                    <BillingTooltipContent side="left" className="max-w-[240px]">
                                                                        <p className="text-[10px] leading-relaxed">Revoke this teacher's access. They will lose premium features immediately.</p>
                                                                    </BillingTooltipContent>
                                                                </Tooltip>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ── Join Existing Faculty (for non-owners, non-trialing, non-linked users) ── */}
                            {!isFacultyOwner && !isFacultyTrialing && (
                                <div className="border border-slate-200 dark:border-white/10 bg-white/80 dark:bg-[#040a1f]/30 p-4 sm:p-6 flex flex-col sm:flex-row items-center gap-4 sm:gap-6 shadow-2xl shadow-blue-900/10">
                                    <div className="flex-1 space-y-2">
                                        <h3 className="text-[10px] font-black tracking-widest uppercase text-slate-900 dark:text-white/90">Join Existing Faculty</h3>
                                        <p className="text-[9px] font-bold text-slate-600 dark:text-slate-500 uppercase tracking-widest leading-relaxed">
                                            Has your principal already remitted the enterprise upgrade? Enter your unique faculty handshake code below to synchronize your account instantly.
                                        </p>
                                    </div>
                                    <div className="flex w-full sm:w-auto gap-2">
                                        <Input
                                            placeholder="Enter Faculty Code (UID)"
                                            value={facultyCode}
                                            onChange={(e) => setFacultyCode(e.target.value)}
                                            className="bg-white/5 border-slate-200 dark:border-white/10 rounded-none h-10 text-[10px] font-mono tracking-widest text-slate-900 dark:text-zinc-100 placeholder:text-zinc-600 focus:border-blue-500 w-full sm:w-64"
                                        />
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <button
                                                    onClick={handleJoinFaculty}
                                                    disabled={isJoiningFaculty || !facultyCode}
                                                    className="h-10 px-6 bg-blue-600 hover:bg-blue-500 text-slate-900 dark:text-white text-[9px] font-black uppercase tracking-widest border border-blue-500 transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
                                                >
                                                    {isJoiningFaculty ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                                                    {isJoiningFaculty ? 'Linking...' : 'Link Account'}
                                                </button>
                                            </TooltipTrigger>
                                            <BillingTooltipContent side="top" className="max-w-[260px]">
                                                <p className="text-[10px] leading-relaxed">Enter the faculty code from your principal to link your account to their faculty subscription.</p>
                                            </BillingTooltipContent>
                                        </Tooltip>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <ConfirmDialog
                    open={adminConfirmDialog.open}
                    title={adminConfirmDialog.isCurrentlyAdmin ? "Revoke Admin Privileges?" : "Give Admin Privileges?"}
                    description={adminConfirmDialog.isCurrentlyAdmin 
                        ? `Are you sure you want to revoke admin rights from ${adminConfirmDialog.email}?` 
                        : `Are you sure you want to give admin rights to ${adminConfirmDialog.email}?`}
                    onConfirm={() => {
                        executeToggleAdmin(adminConfirmDialog.email, adminConfirmDialog.isCurrentlyAdmin);
                        setAdminConfirmDialog({ open: false, email: '', isCurrentlyAdmin: false });
                    }}
                    onCancel={() => setAdminConfirmDialog({ open: false, email: '', isCurrentlyAdmin: false })}
                    confirmText={adminConfirmDialog.isCurrentlyAdmin ? "Revoke Privileges" : "Approve Privileges"}
                    cancelText="Cancel"
                />
            </>
            );
        }

        if ((isFacultyOwner || isFacultyTrialing) && !organizationId && currentUid) {
            return (
                <div className="w-full bg-slate-50 dark:bg-[#020617] text-slate-900 dark:text-zinc-100 min-h-[80vh] py-12 px-4 flex flex-col items-center justify-center font-sans">
                    <div className="max-w-md w-full bg-slate-100 dark:bg-[#0a1024] border border-red-500/40 p-8 shadow-2xl relative overflow-hidden text-center">
                        <div className="absolute top-0 left-0 w-full h-1 bg-red-500/60" />
                        <Building2 className="w-12 h-12 text-red-500/80 mx-auto mb-4" />
                        <h2 className="text-xl font-black uppercase tracking-widest text-slate-900 dark:text-white mb-2">Setup Required</h2>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 tracking-wider leading-relaxed mb-6">
                            You have successfully subscribed to the Faculty Plan! To complete your setup and generate your secure Faculty ID, you must provide your Institution Name.
                        </p>
                        <div className="text-left mb-6">
                            <Label className="text-[9px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-2 block">Institution Name</Label>
                            <Input 
                                value={institutionName}
                                onChange={(e) => setInstitutionName(e.target.value)}
                                placeholder="e.g. Sonoma High School"
                                className="h-12 text-[12px] font-medium bg-white dark:bg-black border-slate-200 dark:border-white/10"
                            />
                        </div>
                        <button
                            onClick={handleSaveOrgDetails}
                            disabled={isSavingOrgDetails || !institutionName.trim()}
                            className="w-full h-12 bg-red-600 hover:bg-red-500 text-white text-[11px] font-black uppercase tracking-widest transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {isSavingOrgDetails ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                            Complete Setup
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <>
            <div className="w-full bg-slate-50 dark:bg-[#020617] text-slate-900 dark:text-zinc-100 py-4 px-3 sm:py-6 sm:px-4 font-sans selection:bg-blue-500/30">
                {/* Compact Header - Centered for better focus */}
                <div className="max-w-[1000px] mx-auto mb-8 sm:mb-12 flex flex-col items-center text-center gap-4 sm:gap-6">
                    <div className="w-full flex items-center justify-between">
                        <div className="text-left">
                            <h1 className="text-xl sm:text-2xl font-black uppercase tracking-tighter italic text-slate-900 dark:text-white/90">Choose Your Plan</h1>
                            <div className="h-[1px] w-12 bg-blue-500/40 my-2" />
                            <p className="text-blue-600/40 dark:text-blue-400/30 text-[9px] font-bold uppercase tracking-[0.2em]">Select a plan for your workspace.</p>
                        </div>
                        {(isFacultyOwner || linkedTo) && (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        onClick={() => setShowFacultyPanel(true)}
                                        className="flex items-center gap-2 h-9 px-4 bg-blue-600/80 hover:bg-blue-500 text-slate-900 dark:text-white text-[9px] font-black uppercase tracking-widest border border-blue-500/50 transition-all active:scale-[0.98] shrink-0"
                                    >
                                        <Users className="w-3.5 h-3.5" />
                                        {isFacultyOwner ? 'Manage Faculty Settings' : 'View Access Details'}
                                    </button>
                                </TooltipTrigger>
                                <BillingTooltipContent side="bottom" className="max-w-[260px]">
                                    <p className="text-[10px] leading-relaxed">
                                        {isFacultyOwner
                                            ? 'Manage your faculty: add teachers to the allowlist, share your faculty code, and manage seats.'
                                            : `You are using access granted by your principal (${linkedTo?.principalEmail || ''}). View details here.`
                                        }
                                    </p>
                                </BillingTooltipContent>
                            </Tooltip>
                        )}
                    </div>

                {/* ═══ FACULTY PLAN PRINCIPAL BANNER ═══ */}
                    {isFacultyOwner && (userStatus === 'active' || userStatus === 'trial') && (
                        <div className="w-full max-w-[800px] border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-500/30 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div className="flex items-start gap-3">
                                <div className="w-9 h-9 rounded-sm bg-blue-100 dark:bg-blue-600/20 flex items-center justify-center shrink-0 mt-0.5">
                                    <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                </div>
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-[8px] font-black uppercase tracking-[0.3em] text-blue-700 dark:text-blue-400">Account Owner</span>
                                        <Badge className="rounded-none text-[8px] font-black px-2 h-4 border-none bg-blue-600 text-white">FACULTY PLAN</Badge>
                                    </div>
                                    <p className="text-[11px] font-black text-slate-900 dark:text-white tracking-tight">You own and manage this Faculty Plan.</p>
                                    <p className="text-[9px] text-slate-600 dark:text-slate-400 uppercase tracking-wider mt-0.5">
                                        {userExpiry && userExpiry < new Date() ? 'Expired on' : 'Expires'}: <span className={`font-mono font-black ${userExpiry && userExpiry > new Date() ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                                            {userExpiry ? userExpiry.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                                        </span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ═══ LINKED FACULTY MEMBER BANNER ═══ */}
                    {linkedTo && !isFacultyOwner && (
                        <div className={`w-full max-w-[800px] border p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
                            linkRevoked
                                ? 'border-l-4 border-red-500 bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-500/30'
                                : 'border-l-4 border-emerald-500 bg-emerald-50 dark:bg-emerald-950/10 border-emerald-200 dark:border-emerald-500/30'
                        }`}>
                            <div className="flex items-start gap-3">
                                <div className={`w-9 h-9 rounded-sm flex items-center justify-center shrink-0 mt-0.5 ${linkRevoked ? 'bg-red-100 dark:bg-red-600/20' : 'bg-emerald-100 dark:bg-emerald-600/20'}`}>
                                    {linkRevoked
                                        ? <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />
                                        : <Info className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                                    }
                                </div>
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className={`text-[8px] font-black uppercase tracking-[0.3em] ${linkRevoked ? 'text-red-600 dark:text-red-400' : 'text-emerald-700 dark:text-emerald-400'}`}>
                                            {linkRevoked ? 'Access Status' : 'Faculty Member'}
                                        </span>
                                        <Badge className={`rounded-none text-[8px] font-black px-2 h-4 border-none ${linkRevoked ? 'bg-red-600 text-white' : 'bg-emerald-600 text-white'}`}>
                                            {linkRevoked ? 'ACCESS REVOKED' : 'USING FACULTY PLAN'}
                                        </Badge>
                                    </div>
                                    {linkRevoked ? (
                                        <p className="text-[11px] font-black text-red-600 dark:text-red-300 tracking-tight">Your faculty access has been revoked by the account owner.</p>
                                    ) : (
                                        <p className="text-[11px] font-black text-slate-900 dark:text-white tracking-tight">
                                            You do not have your own subscription. Your access is granted by the account owner's Faculty Plan.
                                        </p>
                                    )}
                                    <p className="text-[9px] text-slate-600 dark:text-slate-400 uppercase tracking-wider mt-0.5">
                                        {linkRevoked
                                            ? 'Contact the account owner to be re-added, or upgrade to your own plan below.'
                                            : <>Account Owner: <span className="font-mono text-slate-700 dark:text-slate-300">{linkedTo.principalEmail}</span> — If the account owner removes you, your access ends immediately.</>
                                        }
                                    </p>
                                </div>
                            </div>
                            {!linkRevoked && (
                                <button
                                    onClick={() => setShowFacultyPanel(true)}
                                    className="flex items-center justify-center gap-2 h-10 px-6 bg-emerald-600/80 hover:bg-emerald-500 text-white text-[9px] font-black uppercase tracking-widest border border-emerald-500/50 transition-all active:scale-[0.98] shrink-0 w-full sm:w-auto"
                                >
                                    <Info className="w-3.5 h-3.5" />
                                    View Access Details
                                </button>
                            )}
                        </div>
                    )}

                    {/* ═══ PERSISTENT STATUS BAR — always shows current plan + expiry (for non-faculty cases) ═══ */}
                    {!isFacultyOwner && !linkedTo && (
                    <div className="w-full max-w-[800px] bg-slate-100 dark:bg-[#0a1024] border border-slate-200 dark:border-white/10 p-4 flex flex-col sm:flex-row justify-between items-center gap-3">
                        <div className="flex items-center gap-3">
                            <span className="text-[8px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Current Plan:</span>
                            <Badge className={`rounded-none text-[8px] font-black px-2 h-5 border-none ${
                                userStatus === 'active' ? 'bg-emerald-600 text-slate-900 dark:text-white' :
                                userStatus === 'trial' ? 'bg-amber-600 text-slate-900 dark:text-white' :
                                'bg-red-600/80 text-slate-900 dark:text-white'
                            }`}>
                                {effectivePlan === 'school_trial' ? 'FACULTY TRIAL' :
                                 effectivePlan === 'individual_trial' || effectivePlan === 'starter' ? 'INDIVIDUAL TRIAL' :
                                 isFacultyPlanScope(effectivePlan) ? 'FACULTY PLAN' :
                                 isIndividualPlanScope(effectivePlan) ? 'INDIVIDUAL PLAN' :
                                 userStatus === 'expired' ? 'EXPIRED' : effectivePlan.toUpperCase() || 'NONE'}
                            </Badge>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="text-[8px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">
                                {userStatus === 'trial' || userStatus === 'active' ? 'Expires:' : 'Expired:'}
                            </span>
                            <span className={`text-[10px] font-black font-mono tracking-wider ${
                                userExpiry && userExpiry > new Date() ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
                            }`}>
                                {userExpiry
                                    ? userExpiry.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                                    : 'N/A'
                                }
                            </span>
                        </div>
                    </div>
                    )}

                    {/* Tier Toggle */}
                    <div className="flex bg-slate-100 dark:bg-[#0a1024] border border-slate-200 dark:border-white/10 p-1 rounded-none shadow-2xl mt-4 overflow-x-auto">
                        {Object.values(SUBSCRIPTION_TIERS).map((tier) => (
                            <Tooltip key={tier.id}>
                                <TooltipTrigger asChild>
                                    <button
                                        onClick={() => tier.isAvailable && setActiveTier(tier.id as any)}
                                        disabled={!tier.isAvailable}
                                        className={`px-4 sm:px-8 py-2 text-[9px] font-black uppercase tracking-widest transition-all shrink-0 ${
                                            activeTier === tier.id 
                                            ? 'bg-blue-600 text-slate-900 dark:text-white shadow-lg shadow-blue-600/20' 
                                            : !tier.isAvailable
                                            ? 'text-slate-400 dark:text-slate-600 cursor-not-allowed opacity-50'
                                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5'
                                        }`}
                                    >
                                        {tier.name}
                                    </button>
                                </TooltipTrigger>
                                <BillingTooltipContent side="top" className="max-w-[240px]">
                                    <p className="text-[10px] leading-relaxed">{tier.description}</p>
                                </BillingTooltipContent>
                            </Tooltip>
                        ))}
                    </div>

                    {/* Billing Cycle Selector - Centered */}
                    <div className="flex bg-slate-100 dark:bg-[#0a1024] border border-slate-200 dark:border-white/10 p-1 rounded-none shadow-2xl shadow-blue-900/10 mt-6 mt-md-0 overflow-x-auto">
                        {(['monthly', 'termly', 'yearly'] as const)
                            .filter(cycle => {
                                const days = parseInt(pricingState[cycle]?.durationDays || '0', 10);
                                return !isNaN(days) && days > 0;
                            })
                            .map((cycle) => (
                            <Tooltip key={cycle}>
                                <TooltipTrigger asChild>
                                    <button
                                        onClick={() => setBillingCycle(cycle)}
                                        className={`px-3 sm:px-6 py-2 text-[9px] font-black uppercase tracking-widest transition-all shrink-0 ${
                                            billingCycle === cycle 
                                            ? 'bg-blue-600 text-slate-900 dark:text-white shadow-lg shadow-blue-600/20' 
                                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5'
                                        }`}
                                    >
                                        {cycle}
                                    </button>
                                </TooltipTrigger>
                                <BillingTooltipContent side="top">
                                    <p className="text-[10px]">{cycle === 'monthly' ? 'Paid every month — no long-term commitment' : cycle === 'termly' ? 'Paid once per school term' : 'Best value — paid once per year'}</p>
                                </BillingTooltipContent>
                            </Tooltip>
                        ))}
                    </div>
                </div>

                {/* ═══ FREE TRIAL SECTION — Two types: Individual & Faculty ═══ */}
                {billingCycle === 'monthly' && hasExplicitTrial && !hasActivePaidSubscription && !loadingTransactions && (
                    <div className="max-w-[1000px] mx-auto mb-8 p-4 bg-amber-50 border border-amber-200 dark:bg-amber-950/20 dark:border-amber-500/30 flex items-start gap-3">
                        <Info className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                        <div className="space-y-1">
                            <p className="text-[10px] font-black uppercase tracking-widest text-amber-600 dark:text-amber-300">Free trial already claimed</p>
                            <p className="text-[9px] text-slate-500 dark:text-slate-400 uppercase tracking-wider leading-relaxed">
                                This account claimed its free trial on {formatSubscriptionDate(trialClaimedAt)}. Trials are one-time only and cannot be restarted after expiry; choose Individual or Faculty below to continue access.
                            </p>
                        </div>
                    </div>
                )}
                {billingCycle === 'monthly' && canClaimTrial && !hasFutureExpiry && !loadingTransactions && (
                    <div className="max-w-[1000px] mx-auto mb-8">
                        <p className="text-[8px] font-black uppercase tracking-[0.3em] text-blue-500/30 mb-3">Free Trial Options — {trialDays} Days Full Access</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-slate-200/60 dark:border-white/5 bg-white/[0.01]">

                            {/* Individual Trial */}
                            <div className="bg-white p-4 sm:p-5 flex flex-col space-y-3 sm:space-y-4 border-r border-slate-200/60 dark:border-white/5 hover:bg-slate-50 dark:bg-[#040a1f]/30 dark:hover:bg-[#081028]/40 transition-colors">
                                <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                        <User className="w-3.5 h-3.5 text-emerald-500/70" />
                                        <span className="text-[8px] font-black uppercase tracking-widest text-emerald-500/60">Solo Teacher</span>
                                    </div>
                                    <h3 className="text-sm font-black tracking-tight uppercase text-slate-700 dark:text-white/80">Individual Trial</h3>
                                    <p className="text-[8px] text-slate-600 dark:text-slate-500 font-bold uppercase tracking-wider">{trialDays} days — all individual features</p>
                                </div>
                                <div className="space-y-2 flex-grow">
                                    {['Lesson Planning', 'AI Generation', 'PDF Export', 'Assessments'].map((f, i) => (
                                        <div key={i} className="flex items-center gap-2 text-[9px] font-bold text-slate-600 dark:text-slate-500 uppercase tracking-wider">
                                            <Check className="w-2.5 h-2.5 text-emerald-500/50" /> {f}
                                        </div>
                                    ))}
                                </div>
                                {userPlan === 'individual_trial' && userStatus === 'trial' && !isTrialExpired ? (
                                    <div className="w-full text-center py-1">
                                        <span className="text-[9px] font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400">✓ Active</span>
                                    </div>
                                ) : (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button
                                                onClick={() => handleActivateTrial('individual')}
                                                disabled={isActivatingTrial}
                                                className="w-full h-8 rounded-none bg-emerald-600/80 hover:bg-emerald-500 text-slate-900 dark:text-white text-[9px] font-black uppercase tracking-widest border border-emerald-500/50 transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
                                            >
                                                {isActivatingTrial ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                                                Start Individual Trial
                                            </button>
                                        </TooltipTrigger>
                                        <BillingTooltipContent side="top" className="max-w-[260px]">
                                            <p className="text-[10px] leading-relaxed">Try all individual features free for {trialDays} days. No payment required.</p>
                                        </BillingTooltipContent>
                                    </Tooltip>
                                )}
                            </div>

                            {/* Faculty Trial */}
                            <div className="bg-white p-4 sm:p-5 flex flex-col space-y-3 sm:space-y-4 hover:bg-slate-50 dark:bg-[#040a1f]/30 dark:hover:bg-[#081028]/40 transition-colors">
                                <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                        <Building2 className="w-3.5 h-3.5 text-blue-500/70" />
                                        <span className="text-[8px] font-black uppercase tracking-widest text-blue-500/60">Account Owner / Faculty</span>
                                    </div>
                                    <h3 className="text-sm font-black tracking-tight uppercase text-slate-700 dark:text-white/80">Faculty Trial</h3>
                                    <p className="text-[8px] text-slate-600 dark:text-slate-500 font-bold uppercase tracking-wider">{trialDays} days — manage your teaching staff</p>
                                </div>
                                <div className="space-y-2 flex-grow">
                                    {['All Individual Features', 'Faculty Email Allowlist', 'Faculty Code Sharing', 'Seat Management'].map((f, i) => (
                                        <div key={i} className="flex items-center gap-2 text-[9px] font-bold text-slate-600 dark:text-slate-500 uppercase tracking-wider">
                                            <Check className="w-2.5 h-2.5 text-blue-500/50" /> {f}
                                        </div>
                                    ))}
                                </div>
                                {userPlan === 'school_trial' && userStatus === 'trial' && !isTrialExpired ? (
                                    <div className="w-full text-center py-1">
                                        <span className="text-[9px] font-black uppercase tracking-widest text-blue-600 dark:text-blue-400">✓ Active — Set up faculty below</span>
                                    </div>
                                ) : (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button
                                                onClick={() => handleActivateTrial('faculty')}
                                                disabled={isActivatingTrial}
                                                className="w-full h-8 rounded-none bg-blue-600/80 hover:bg-blue-500 text-slate-900 dark:text-white text-[9px] font-black uppercase tracking-widest border border-blue-500/50 transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
                                            >
                                                {isActivatingTrial ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                                                Start Faculty Trial
                                            </button>
                                        </TooltipTrigger>
                                        <BillingTooltipContent side="top" className="max-w-[280px]">
                                            <p className="text-[10px] leading-relaxed">Try all faculty features free for {trialDays} days. Manage your teaching staff with email allowlist and seat management.</p>
                                        </BillingTooltipContent>
                                    </Tooltip>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* ═══ PAID PLANS GRID — dynamically rendered from SUBSCRIPTION_TIERS config ═══ */}
                {(() => {
                    const tier = SUBSCRIPTION_TIERS[activeTier];
                    if (!tier) return null;
                    const isTierAvailable = tier.isAvailable;

                    // Compute display prices: Tier 1 uses live Firestore pricing; other tiers use static config
                    const indPrice = activeTier === 'tier1'
                        ? pricingState[billingCycle].individual
                        : tier.variants.individual[billingCycle].price.toLocaleString();
                    const fclPrice = activeTier === 'tier1'
                        ? pricingState[billingCycle].school
                        : tier.variants.faculty[billingCycle].price.toLocaleString();
                    const priceLabel = pricingState[billingCycle].label;

                    return (
                    <div className="max-w-[1000px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-0 border border-slate-200/60 dark:border-white/5 bg-white/[0.01]">
                        {/* Individual Plan Card */}
                        <div className={`${!isTierAvailable ? 'opacity-60' : ''} bg-white dark:bg-[#081028]/60 p-4 sm:p-5 flex flex-col space-y-4 sm:space-y-5 border-r border-slate-200 dark:border-white/10 relative group overflow-hidden shadow-2xl shadow-blue-900/10`}>
                            <div className="absolute top-0 left-0 w-full h-[1px] bg-blue-500/40" />
                            {!isTierAvailable && (
                                <div className="absolute top-2 right-2">
                                    <span className="text-[7px] font-black uppercase tracking-widest bg-slate-200 dark:bg-white/10 text-slate-500 dark:text-slate-400 px-2 py-0.5">Coming Soon</span>
                                </div>
                            )}
                            <div className="space-y-2">
                                <span className="text-[8px] font-black uppercase tracking-widest text-blue-600 dark:text-blue-400 block tracking-[0.3em]">For Individual Teachers</span>
                                <h3 className="text-sm font-black tracking-tight flex items-center gap-2 uppercase text-slate-900 dark:text-white">
                                    Individual — {tier.name}
                                </h3>
                                <div className="flex items-baseline gap-1 py-1">
                                    <span className="text-xl font-bold font-mono text-slate-900 dark:text-zinc-100">{indPrice}</span>
                                    <span className="text-blue-500/60 dark:text-blue-400/40 text-[8px] font-black uppercase tracking-widest">{priceLabel}</span>
                                </div>
                            </div>

                            <div className="space-y-2 flex-grow">
                                {tier.features.map((f, i) => (
                                    <div key={i} className="flex items-center gap-2 text-[9px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                                        <Check className="w-2.5 h-2.5 text-blue-600 dark:text-blue-500" />
                                        {f}
                                    </div>
                                ))}
                            </div>

                            <button
                                onClick={() => isTierAvailable && selectPlanAndProceed('individual')}
                                disabled={!isTierAvailable}
                                className={`w-full h-8 rounded-none text-[9px] font-black uppercase tracking-widest transition-all active:scale-[0.98] shadow-lg shadow-blue-600/20 ${
                                    isTierAvailable
                                        ? 'bg-blue-600 hover:bg-blue-500 text-slate-900 dark:text-white'
                                        : 'bg-slate-200 dark:bg-white/5 text-slate-400 cursor-not-allowed'
                                }`}
                            >
                                {isTierAvailable ? 'Select Plan' : 'Coming Soon'}
                            </button>
                        </div>

                        {/* Faculty / Enterprise Plan Card */}
                        <div className={`${!isTierAvailable ? 'opacity-60' : ''} ${isFacultyOwner && activeTier === 'tier1' ? 'bg-white dark:bg-[#081028]/60 border border-blue-200 dark:border-blue-500/30' : 'bg-white dark:bg-[#040a1f]/30 border border-slate-200 dark:border-white/10'} p-4 sm:p-5 flex flex-col space-y-4 sm:space-y-5 hover:bg-slate-50 dark:hover:bg-[#081028]/40 transition-colors group text-slate-600 dark:text-zinc-400 relative`}>
                            {!isTierAvailable && (
                                <div className="absolute top-2 right-2">
                                    <span className="text-[7px] font-black uppercase tracking-widest bg-slate-200 dark:bg-white/10 text-slate-500 dark:text-slate-400 px-2 py-0.5">Coming Soon</span>
                                </div>
                            )}
                            <div className="space-y-2">
                                <span className="text-[8px] font-black uppercase tracking-widest text-blue-600 dark:text-blue-400 block">
                                    {isFacultyOwner && activeTier === 'tier1' ? 'Current Plan' : 'Enterprise / Hub'}
                                </span>
                                <h3 className="text-sm font-black tracking-tight uppercase text-slate-900 dark:text-white flex items-center gap-2">
                                    Faculty — {tier.name}
                                    {isFacultyOwner && hasFutureExpiry && activeTier === 'tier1' && (
                                        <Badge className="rounded-none bg-blue-600 text-white text-[7px] font-black px-1 h-3 border-none">ACTIVE</Badge>
                                    )}
                                </h3>
                                <div className="flex items-baseline gap-1 py-1">
                                    <span className="text-xl font-bold font-mono text-slate-900 dark:text-white/80">{fclPrice}</span>
                                    <span className="text-blue-600/60 dark:text-blue-500/20 text-[8px] font-black uppercase tracking-widest">{priceLabel}</span>
                                </div>
                            </div>

                            <div className="space-y-2 flex-grow">
                                <div className="flex items-center gap-2 text-[9px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-wider mb-3">
                                    Everything in Individual, plus:
                                </div>
                                <div className="flex items-center gap-2 text-[9px] font-bold text-slate-700 dark:text-slate-500 uppercase tracking-wider">
                                    <div className="w-1 h-1 bg-blue-600 dark:bg-blue-500/40" />
                                    School Pack (Faculty Management) — Up to {maxSeats} seats
                                </div>
                                <div className="flex items-center gap-2 text-[9px] font-bold text-slate-700 dark:text-slate-500 uppercase tracking-wider">
                                    <div className="w-1 h-1 bg-blue-600 dark:bg-blue-500/40" />
                                    Custom School Website (Design, Security & Maintenance)
                                </div>
                            </div>

                            <button
                                onClick={() => {
                                    if (!isTierAvailable) return;
                                    if (isFacultyOwner && userStatus === 'active' && hasFutureExpiry && activeTier === 'tier1') {
                                        setShowFacultyPanel(true);
                                    } else {
                                        selectPlanAndProceed('school');
                                    }
                                }}
                                disabled={!isTierAvailable}
                                className={`w-full h-8 rounded-none border transition-all active:scale-[0.98] text-[9px] font-black uppercase tracking-widest ${
                                    !isTierAvailable
                                        ? 'bg-transparent border-slate-200/60 dark:border-white/5 text-slate-400 cursor-not-allowed'
                                        : isFacultyOwner && userStatus === 'active' && hasFutureExpiry && activeTier === 'tier1'
                                        ? 'bg-blue-600 hover:bg-blue-500 border-blue-500/50 text-white'
                                        : 'border-slate-200/60 dark:border-white/5 bg-transparent hover:bg-slate-100 dark:hover:bg-white/5 text-slate-600 dark:text-slate-300'
                                }`}
                            >
                                {!isTierAvailable
                                    ? 'Coming Soon'
                                    : isFacultyOwner && userStatus === 'active' && hasFutureExpiry && activeTier === 'tier1'
                                    ? 'Manage Faculty Settings'
                                    : 'Select Plan'
                                }
                            </button>
                        </div>
                    </div>
                    );
                })()}


                {paymentSuccess && !successBannerDismissed && (
                    <div className="max-w-[1000px] mx-auto mt-6 p-3 sm:p-4 bg-emerald-600/20 border border-emerald-500/40 animate-in fade-in">
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3">
                                <Check className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700 dark:text-emerald-400">Payment successful — your subscription is now active.</p>
                                    <p className="text-[9px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-200/70 mt-1">
                                        Your subscription will expire on {formatSubscriptionDate(userExpiry)}.
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setSuccessBannerDismissed(true)}
                                className="text-emerald-500 dark:text-emerald-300/60 hover:text-slate-900 dark:hover:text-white transition-colors"
                                aria-label="Dismiss payment success banner"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
                {!loadingTransactions && (
                    <div className="max-w-[1000px] mx-auto mt-6 border border-slate-200 dark:border-white/10 bg-white/80 dark:bg-[#040a1f]/30 overflow-hidden">
                        <button
                            onClick={() => setShowPaymentHistory(!showPaymentHistory)}
                            className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-100 dark:hover:bg-white/[0.02] transition-colors"
                        >
                            <span className="text-[9px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Payment History & Attempts ({transactions.length})</span>
                            <span className="text-[8px] text-slate-600 dark:text-slate-500">{showPaymentHistory ? '▲' : '▼'}</span>
                        </button>
                        {showPaymentHistory && (
                            <div className="border-t border-slate-200/60 dark:border-white/5 divide-y divide-slate-100 dark:divide-white/5 max-h-64 overflow-y-auto">
                                {transactions.length === 0 ? (
                                    <div className="p-4 text-center text-[9px] text-slate-600 dark:text-slate-500">No transactions yet.</div>
                                ) : (
                                    transactions.map(tx => (
                                        <div key={tx.id} className="px-4 py-2.5 flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-3 text-[9px]">
                                            <div className="flex items-center gap-3">
                                                <span className={`font-black uppercase tracking-wider ${tx.type === 'mpesa' ? 'text-blue-600 dark:text-blue-400' : 'text-purple-600 dark:text-purple-400'}`}>{tx.type === 'mpesa' ? 'M-Pesa' : 'PesaPal'}</span>
                                                <span className="text-slate-500 dark:text-slate-400 font-mono">{tx.planId?.replace(/_/g, ' ').toUpperCase() || '—'}</span>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <span className={`text-[8px] font-black uppercase tracking-widest ${
                                                    tx.status === 'completed' ? 'text-emerald-600 dark:text-emerald-400' :
                                                    tx.status === 'pending' ? 'text-amber-600 dark:text-amber-400' :
                                                    'text-red-600 dark:text-red-400'
                                                }`}>{tx.status || 'recorded'}</span>
                                                <span className="font-mono text-slate-800 dark:text-zinc-200">KES {Number(tx.amount || 0)?.toLocaleString()}</span>
                                                <span className="text-slate-600 dark:text-slate-500">{tx.date?.toLocaleDateString?.() || ''}</span>
                                                {tx.receiptNumber && <span className="text-slate-600 dark:text-slate-500 font-mono text-[8px]">#{tx.receiptNumber}</span>}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                )}
                {paymentConfirmDialog.open && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
                        <div className="w-full max-w-sm bg-slate-100 dark:bg-[#0a1024] border border-emerald-500/30 shadow-2xl p-4 sm:p-6 text-center animate-in zoom-in-95 duration-300 mx-4">
                            <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mx-auto mb-4">
                                <Check className="w-6 h-6" />
                            </div>
                            <h2 className="text-lg font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400 mb-2">Payment Successful</h2>
                            <div className="space-y-2 text-sm text-slate-700 dark:text-slate-300 mb-6 bg-white dark:bg-white/5 p-4 border border-slate-200/60 dark:border-white/5 text-left">
                                <div className="flex justify-between">
                                    <span className="text-slate-500 dark:text-slate-400 uppercase text-[10px] tracking-widest font-bold">Method</span>
                                    <span className="font-mono">{paymentConfirmDialog.method}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-slate-500 dark:text-slate-400 uppercase text-[10px] tracking-widest font-bold">Plan</span>
                                    <span className="font-mono">{getPlanDisplayName(paymentConfirmDialog.plan)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-slate-500 dark:text-slate-400 uppercase text-[10px] tracking-widest font-bold">Expiry</span>
                                    <span className="font-mono">{formatSubscriptionDate(paymentConfirmDialog.expiry)}</span>
                                </div>
                            </div>
                            <button
                                onClick={() => window.location.reload()}
                                className="w-full h-10 bg-emerald-600 hover:bg-emerald-500 text-slate-900 dark:text-white text-[11px] font-black uppercase tracking-[0.2em] transition-all"
                            >
                                Continue to Dashboard
                            </button>
                        </div>
                    </div>
                )}

             </div>
            <ConfirmDialog
                open={adminConfirmDialog.open}
                title={adminConfirmDialog.isCurrentlyAdmin ? "Revoke Admin Privileges?" : "Give Admin Privileges?"}
                description={adminConfirmDialog.isCurrentlyAdmin 
                    ? `Are you sure you want to revoke admin rights from ${adminConfirmDialog.email}?` 
                    : `Are you sure you want to give admin rights to ${adminConfirmDialog.email}?`}
                onConfirm={() => {
                    executeToggleAdmin(adminConfirmDialog.email, adminConfirmDialog.isCurrentlyAdmin);
                    setAdminConfirmDialog({ open: false, email: '', isCurrentlyAdmin: false });
                }}
                onCancel={() => setAdminConfirmDialog({ open: false, email: '', isCurrentlyAdmin: false })}
                confirmText={adminConfirmDialog.isCurrentlyAdmin ? "Revoke Privileges" : "Approve Privileges"}
                cancelText="Cancel"
            />
            </>
        );
    }

    return (
        <div className="w-full bg-slate-50 dark:bg-[#020617] text-slate-900 dark:text-zinc-100 py-4 px-3 sm:py-6 sm:px-4 font-sans selection:bg-blue-500/30">
            <div className="max-w-[1000px] mx-auto">
                {/* Compact Back Link */}
                <button 
                    onClick={() => setView('pricing')}
                    className="flex items-center gap-2 text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white mb-6 uppercase tracking-[0.2em] text-[10px] font-black transition-all"
                >
                    <ArrowLeft className="w-3 h-3" /> Back
                </button>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 border border-slate-200 dark:border-white/10">
                    
                    {/* Summary (Dense) */}
                    <div className="lg:col-span-4 bg-slate-100/50 dark:bg-[#040a1f]/50 p-4 sm:p-6 border-b lg:border-b-0 lg:border-r border-slate-200 dark:border-white/10 animate-in fade-in duration-300">
                        <div className="space-y-6">
                            <div className="space-y-1">
                                <h2 className="text-[9px] font-black uppercase tracking-[0.3em] text-blue-500/40">Order Summary</h2>
                                <h1 className="text-lg font-black italic tracking-tighter uppercase truncate overflow-ellipsis text-slate-900 dark:text-white/90">
                                    {selectedPlan === 'individual' ? 'Individual' : 'Faculty'} — {SUBSCRIPTION_TIERS[activeTier]?.name || activeTier}
                                </h1>
                            </div>

                            <div className="pt-4 border-t border-slate-200 dark:border-white/10">
                                <div className="flex justify-between items-end">
                                    <div className="space-y-0.5">
                                        <p className="text-[8px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">{pricingState[billingCycle].cycleLabel}</p>
                                        <p className="text-2xl font-black font-mono tracking-tighter text-slate-900 dark:text-white">
                                            {activeTier === 'tier1'
                                                ? (selectedPlan === 'individual' ? pricingState[billingCycle].individual : pricingState[billingCycle].school)
                                                : (selectedPlan === 'individual'
                                                    ? SUBSCRIPTION_TIERS[activeTier]?.variants.individual[billingCycle].price.toLocaleString()
                                                    : SUBSCRIPTION_TIERS[activeTier]?.variants.faculty[billingCycle].price.toLocaleString())
                                            }
                                        </p>
                                    </div>
                                    <p className="text-[8px] font-black uppercase text-blue-900 dark:text-blue-300 mb-1 leading-none">KSH</p>
                                </div>
                            </div>

                            <div className="space-y-4 pt-6 border-t border-slate-200 dark:border-white/10">
                                <div className="flex items-center gap-2 text-[8px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">
                                    <ShieldCheck className="w-3 h-3" /> Secure Payment
                                </div>
                                <div className="flex items-center gap-2 text-[8px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">
                                    <Globe className="w-3 h-3" /> Works Nationwide
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Payment (Dense) */}
                    <div className="lg:col-span-8 bg-slate-50 dark:bg-[#020617] p-4 sm:p-8 animate-in fade-in duration-300">
                        {/* Payment method tabs hidden — PesaPal only */}
                        {false && (
                        <div className="flex border border-slate-200 dark:border-white/10 mb-8 overflow-x-auto bg-white/5">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button 
                                        onClick={() => setMethod('card')}
                                        className={`flex-1 py-2 text-[9px] font-black uppercase tracking-[0.2em] transition-all ${method === 'card' ? 'bg-blue-600 text-slate-900 dark:text-white' : 'text-slate-600 dark:text-zinc-300 hover:text-slate-700 dark:hover:text-white'}`}
                                    >Card Source</button>
                                </TooltipTrigger>
                                <BillingTooltipContent side="bottom">
                                    <p className="text-[10px]">Pay using credit or debit card via PesaPal secure checkout.</p>
                                </BillingTooltipContent>
                            </Tooltip>

                        {/* M-Pesa Link tab — hidden; leaving only PesaPal Card Source */}
                        {/* Sandbox Fill tab — hidden; dev only */}

                        </div>
                        )}

                        {method === 'card' ? (
                            <div className="space-y-6">
                                {/* PesaPal iframe overlay */}
                                {pesapalIframeUrl && (
                                    <div className="fixed inset-0 z-50 flex flex-col bg-black/90">
                                        <div className="flex justify-between items-center px-4 py-3 bg-[#040a1f] border-b border-slate-200 dark:border-white/10">
                                            <span className="text-[9px] font-black uppercase tracking-widest text-blue-600 dark:text-blue-400">PesaPal Secure Checkout</span>
                                            <button
                                                onClick={() => { setPesapalIframeUrl(null); setPesapalOrderId(null); }}
                                                className="text-[9px] font-black uppercase tracking-widest text-slate-600 dark:text-slate-500 hover:text-red-400 transition-colors"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                        <iframe
                                            src={pesapalIframeUrl}
                                            className="flex-1 w-full border-none"
                                            title="PesaPal Checkout"
                                            allow="payment"
                                        />
                                    </div>
                                )}

                                <div className="p-3 bg-blue-600/10 border border-blue-500/20 text-center">
                                    <p className="text-[9px] font-black uppercase tracking-widest text-blue-600 dark:text-blue-400">PesaPal Secure Payment</p>
                                    <p className="text-[8px] text-slate-600 dark:text-slate-500 mt-1 uppercase tracking-wider">
                                        Enter billing details to open the secure PesaPal checkout.
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="grid gap-1.5">
                                        <Label className="text-[8px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">First Name</Label>
                                        <Input
                                            placeholder="John"
                                            value={cardFirstName}
                                            onChange={(e) => setCardFirstName(e.target.value)}
                                            className="h-8 border-slate-200 dark:border-white/10 bg-white/5 rounded-none text-[10px] font-mono tracking-widest text-slate-800 dark:text-zinc-200 placeholder:text-zinc-600 focus:border-blue-500"
                                        />
                                    </div>
                                    <div className="grid gap-1.5">
                                        <Label className="text-[8px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Last Name</Label>
                                        <Input
                                            placeholder="Doe"
                                            value={cardLastName}
                                            onChange={(e) => setCardLastName(e.target.value)}
                                            className="h-8 border-slate-200 dark:border-white/10 bg-white/5 rounded-none text-[10px] font-mono tracking-widest text-slate-800 dark:text-zinc-200 placeholder:text-zinc-600 focus:border-blue-500"
                                        />
                                    </div>
                                    <div className="grid gap-1.5 col-span-2">
                                        <Label className="text-[8px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Email Address</Label>
                                        <Input
                                            type="email"
                                            placeholder="john.doe@example.com"
                                            value={cardEmail}
                                            onChange={(e) => setCardEmail(e.target.value)}
                                            className="h-8 border-slate-200 dark:border-white/10 bg-white/5 rounded-none text-[10px] font-mono tracking-widest text-slate-800 dark:text-zinc-200 placeholder:text-zinc-600 focus:border-blue-500"
                                        />
                                    </div>
                                    <div className="grid gap-1.5 col-span-2">
                                        <Label className="text-[8px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Phone Number (Optional)</Label>
                                        <Input
                                            type="tel"
                                            placeholder="0700000000"
                                            value={cardPhone}
                                            onChange={(e) => setCardPhone(e.target.value)}
                                            className="h-8 border-slate-200 dark:border-white/10 bg-white/5 rounded-none text-[10px] font-mono tracking-widest text-slate-800 dark:text-zinc-200 placeholder:text-zinc-600 focus:border-blue-500"
                                        />
                                    </div>
                                </div>

                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <button
                                            onClick={handlePesapalPayment}
                                            disabled={isProcessingPayment || (!cardEmail && !cardPhone)}
                                            className="w-full h-9 bg-blue-600 hover:bg-blue-500 text-slate-900 dark:text-white text-[10px] font-black uppercase tracking-[0.3em] transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
                                        >
                                            {isProcessingPayment ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                                            {isProcessingPayment ? 'Connecting to PesaPal...' : 'Proceed to PesaPal Checkout'}
                                        </button>
                                    </TooltipTrigger>
                                    <BillingTooltipContent side="bottom" className="max-w-[260px]">
                                        <p className="text-[10px] leading-relaxed">Open the secure PesaPal checkout page to complete your card payment.</p>
                                    </BillingTooltipContent>
                                </Tooltip>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                <div className="grid gap-1.5">
                                    <Label className="text-[8px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Phone Number</Label>
                                    <Input
                                        name="phone"
                                        placeholder="07XX XXX XXX / 254..."
                                        value={state.phone}
                                        onChange={handleInputChange}
                                        className="h-10 border-slate-200 dark:border-white/10 bg-white/5 rounded-none text-xl font-mono tracking-widest text-slate-800 dark:text-zinc-200 placeholder-white/20 focus:border-blue-500"
                                    />
                                </div>
                                <div className="p-3 bg-white/5 border border-slate-200 dark:border-white/10 text-center">
                                    <p className="text-[8px] text-slate-500 dark:text-slate-400 font-bold uppercase tracking-[0.2em] leading-relaxed">
                                        You will receive a payment prompt on your phone.
                                    </p>
                                </div>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <button 
                                            onClick={handleMpesaPayment}
                                            disabled={isProcessingPayment}
                                            className="w-full h-9 bg-blue-600 hover:bg-blue-500 text-slate-900 dark:text-white text-[10px] font-black uppercase tracking-[0.3em] transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
                                        >
                                            {isProcessingPayment ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                                            {isProcessingPayment ? 'Sending...' : 'Pay with M-Pesa'}
                                        </button>
                                    </TooltipTrigger>
                                    <BillingTooltipContent side="bottom" className="max-w-[260px]">
                                        <p className="text-[10px] leading-relaxed">Send an STK Push prompt to your M-Pesa registered phone number to complete payment.</p>
                                    </BillingTooltipContent>
                                </Tooltip>
                                {stkPushSent && (
                                    <div className="p-3 bg-emerald-600/20 border border-emerald-500/40 text-center animate-in fade-in">
                                        <p className="text-[9px] font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400 flex items-center justify-center gap-2">
                                            <Check className="w-3.5 h-3.5" /> STK Push Sent — check your phone for the M-Pesa PIN prompt
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <ConfirmDialog
                open={adminConfirmDialog.open}
                title={adminConfirmDialog.isCurrentlyAdmin ? "Revoke Admin Privileges?" : "Give Admin Privileges?"}
                description={adminConfirmDialog.isCurrentlyAdmin 
                    ? `Are you sure you want to revoke admin rights from ${adminConfirmDialog.email}?` 
                    : `Are you sure you want to give admin rights to ${adminConfirmDialog.email}?`}
                onConfirm={() => {
                    executeToggleAdmin(adminConfirmDialog.email, adminConfirmDialog.isCurrentlyAdmin);
                    setAdminConfirmDialog({ open: false, email: '', isCurrentlyAdmin: false });
                }}
                onCancel={() => setAdminConfirmDialog({ open: false, email: '', isCurrentlyAdmin: false })}
                confirmText={adminConfirmDialog.isCurrentlyAdmin ? "Revoke Privileges" : "Approve Privileges"}
                cancelText="Cancel"
            />
        </div>
    );
};

export default IndependentBillingView;
